import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import WalterLoader from '../shared/WalterLoader';
import { computeAnnualVolumeM3, getReferenceConsumption } from '../../utils/consumptionCalculations';
import UserDistributionPanel, { isEquipmentAllowed } from './distribution/UserDistributionPanel';
import HotWaterRatioInput, { HOT_WATER_SOURCE_LABELS } from '../auditPages/waterpointForm/HotWaterRatioInput';
import { getHotWaterRatioWithSource } from './actionPlan/calculations/waterPricing';
import UsageCategoryPicker from '../shared/UsageCategoryPicker';
import { useAppMetadata } from '../shared/useAppMetadata';
import { getFunctionalUnitTagStyle } from '../shared/FunctionalUnitTag';
import metadata from '../../metadata.json';
import '../../styles/ClientDashboard.css';

/* ─────────────────────────────────────────────
   Constants
───────────────────────────────────────────── */

const EQUIPMENT_META = {
  faucet:  { label: 'Robinet / Lavabo', icon: '🚿' },
  wc:      { label: 'WC',               icon: '🚽' },
  urinal:  { label: 'Urinoir',          icon: '🚽' },
  shower:  { label: 'Douche',           icon: '🚿' },
  machine: { label: 'Machine',          icon: '⚙️' },
};

/* faucet/shower sont calculés à partir d'un débit (L/min) : la saisie est donc une durée
   cumulée en minutes/jour. wc/urinal/machine sont calculés à partir d'un volume fixe par
   utilisation (chasse, cycle) : la saisie est donc un nombre de fois par jour. */
const DAILY_USAGE_LABELS = {
  faucet:  'Minutes / jour',
  wc:      'Chasses / jour',
  urinal:  'Chasses / jour',
  shower:  'Minutes / jour',
  machine: 'Cycles / jour',
};

const DAILY_USAGE_HINTS = {
  faucet:  'Durée cumulée d\'utilisation du robinet, chaque jour (en minutes)',
  wc:      'Nombre de chasses tirées, chaque jour',
  urinal:  'Nombre de chasses tirées, chaque jour',
  shower:  'Durée cumulée des douches, chaque jour (en minutes)',
  machine: 'Nombre de cycles lancés, chaque jour',
};

/* Granularité de saisie : les durées (minutes) tolèrent les demi-minutes, les cycles sont entiers. */
const DAILY_USAGE_STEP = {
  faucet: 0.5,
  shower: 0.5,
  wc: 1,
  urinal: 1,
  machine: 1,
};

/* Même unité que DAILY_USAGE_LABELS, déclinée à l'année pour l'aperçu de calcul en direct. */
const ANNUAL_USAGE_LABELS = {
  faucet:  'minutes / an',
  wc:      'chasses / an',
  urinal:  'chasses / an',
  shower:  'minutes / an',
  machine: 'cycles / an',
};

/* Aperçu en direct du nombre total d'activations (minutes ou cycles selon l'équipement) sur
   l'année, à partir des mêmes règles que consumptionCalculations.js (nbReelWeeks pondéré par
   les semaines d'activité intense/réduite) — exprimé dans l'unité brute de daily_usage plutôt
   qu'en litres, pour vérifier d'un coup d'œil la cohérence des fréquences saisies. */
const computeAnnualActivations = (usageData) => {
  const { daily_usage, daily_usage_type, nb_users, weekly_usage, yearly_usage, less_intense_weeks, more_intense_weeks } = usageData;
  if (!daily_usage || !weekly_usage || !yearly_usage) return null;

  const lessWeeks = less_intense_weeks || 0;
  const moreWeeks = more_intense_weeks || 0;
  const nbReelWeeks = (yearly_usage - lessWeeks - moreWeeks)
    + moreWeeks * metadata.more_intense_week_effect
    + lessWeeks * metadata.less_intense_week_effect;

  const dailyAmount = (daily_usage_type || 'per_person') === 'per_person' ? daily_usage * (nb_users || 0) : daily_usage;
  const annualAmount = dailyAmount * weekly_usage * nbReelWeeks;
  if (!annualAmount || isNaN(annualAmount)) return null;

  return { annualAmount, perDayAverage: annualAmount / 365 };
};

/* Présélections d'eau chaude par type d'équipement — remplacent les raccourcis génériques
   0/50/100% de HotWaterRatioInput par des scénarios concrets et reconnaissables. */
// Part d'eau sortie du ballon (≈ 55 °C) : une douche à 38-40 °C en prend ~65 %.
const HOT_WATER_PRESETS = {
  faucet: [
    { value: 20,  label: 'Lave-mains',    icon: '🧼' },
    { value: 80,  label: 'Vaisselle',     icon: '🍽️' },
    { value: 50,  label: 'Salle de bain', icon: '🛁' },
    { value: 0,   label: 'Aucune',        icon: '❄️' },
  ],
  shower: [
    { value: 65,  label: 'Douche',         icon: '🚿' },
    { value: 75,  label: 'Douche chaude',  icon: '🌡️' },
    { value: 0,   label: 'Aucune',         icon: '❄️' },
  ],
  machine: [
    { value: 100, label: 'Eau chauffée',    icon: '🔥' },
    { value: 50,  label: 'Double arrivée',  icon: '🌡️' },
    { value: 0,   label: 'Eau froide',      icon: '❄️' },
  ],
  wc:     [{ value: 0, label: 'Aucune', icon: '❄️' }],
  urinal: [{ value: 0, label: 'Aucune', icon: '❄️' }],
};

/* ── Fields eligible for bulk edit (top-level usage fields vs. usage_data fields) ── */
const BULK_FIELD_CONFIG = {
  type_usage:          { label: "Type d'usage",            type: 'usage_category', group: 'top' },
  weekly_usage:         { label: 'Jours par semaine',        type: 'number', min: 0, max: 7,  group: 'data' },
  yearly_usage:         { label: 'Semaines par an',          type: 'number', min: 1, max: 52, group: 'data' },
  more_intense_weeks:   { label: "Sem. d'activité intense",  type: 'number', min: 0, max: 52, group: 'data' },
  less_intense_weeks:   { label: 'Sem. d\'activité réduite', type: 'number', min: 0, max: 52, group: 'data' },
  hot_water_ratio:      { label: 'Part d\'eau chaude (%)',    type: 'number', min: 0, max: 100, group: 'top' },
  water_quality_needed: { label: 'Eau en entrée',            type: 'select', options: [
    { value: 'drinkable', label: 'Potable' }, { value: 'non_drinkable', label: 'Non potable' },
  ], group: 'top' },
  water_quality_out:    { label: 'Eau en sortie',            type: 'select', options: [
    { value: 'drinkable', label: 'Potable' }, { value: 'non_drinkable', label: 'Non potable' },
  ], group: 'top' },
  possible_savings:     { label: 'Économies possibles',      type: 'boolean', group: 'top' },
};

const getBulkFieldValue = (usage, key) => {
  const cfg = BULK_FIELD_CONFIG[key];
  return cfg.group === 'data' ? usage.usage_data?.[key] : usage[key];
};

const formatBulkFieldValue = (key, value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  const cfg = BULK_FIELD_CONFIG[key];
  if (cfg?.type === 'select') return cfg.options?.find(o => o.value === String(value))?.label || String(value);
  return String(value);
};

const getBulkFieldValueCounts = (key, usages) => {
  const values = usages.map(u => getBulkFieldValue(u, key));
  const countMap = values.reduce((acc, v) => {
    const k = String(v);
    if (!acc[k]) acc[k] = { value: v, count: 0 };
    acc[k].count++;
    return acc;
  }, {});
  return Object.values(countMap);
};

const USAGE_PALETTE = [
  '#0097d6', '#051b71', '#10b981', '#f59e0b', '#8b5cf6',
  '#ef4444', '#f97316', '#14b8a6', '#6366f1', '#ec4899',
  '#06b6d4', '#84cc16', '#a855f7', '#f43f5e', '#0ea5e9',
];

const FIELD_CONFIG = {
  faucet_type:      { label: 'Type de robinet',        type: 'select', options: [
    { value: 'technical',      label: 'Technique' },
    { value: 'hand_wash',      label: 'Lave-mains' },
    { value: 'changing_room',  label: 'Vestiaire / Salle de bain' },
    { value: 'other',          label: 'Autre' },
  ]},
  type_machine:     { label: 'Type de machine',         type: 'select', options: [
    { value: 'washing_machine',       label: 'Machine à laver' },
    { value: 'dishwasher',            label: 'Lave-vaisselle pro' },
    { value: 'dishwasher_domestic',   label: 'Lave-vaisselle domestique' },
    { value: 'pressure_washer',       label: 'Karcher' },
    { value: 'other',                 label: 'Autre' },
  ]},
  activation_mode:  { label: "Mode d'activation",      type: 'select', options: [
    { value: 'double_handle', label: 'Double poignée' },
    { value: 'single_handle', label: 'Poignée unique' },
    { value: 'infrared',      label: 'Infrarouge' },
    { value: 'pedal',         label: 'Pédale' },
    { value: 'push_button',   label: 'Bouton poussoir' },
    { value: 'other',         label: 'Autre' },
  ]},
  water_quality_in: { label: "Qualité de l'eau",        type: 'select', options: [
    { value: 'drinkable',     label: 'Potable' },
    { value: 'non_drinkable', label: 'Non potable' },
  ]},
  nb_equipment:     { label: "Nombre d'équipements",   type: 'number' },
  max_flow:         { label: 'Débit maximal',           type: 'number', unitField: 'max_flow_unit' },
  max_flow_unit:    { hidden: true },
  flush_volume:     { label: 'Volume de chasse (L)',    type: 'number' },
  flush_volume_max: { label: 'Volume max chasse (L)',   type: 'number' },
  flush_volume_min: { label: 'Volume min chasse (L)',   type: 'number' },
  volume_per_cycle: { label: 'Volume / débit',          type: 'number', unitField: 'volume_per_cycle_unit' },
  volume_per_cycle_unit: { hidden: true },
  timer_duration:   { label: 'Durée minuteur (min)',    type: 'number' },
  foamer:           { label: 'Mousseur',                type: 'boolean' },
  hot_water:        { label: 'Raccordé eau chaude / eau chauffée', type: 'boolean' },
  hot_water_ratio:  { label: "Part d'eau chaude typique (%)", type: 'number' },
  isolation_valve:  { label: "Vanne d'isolement",       type: 'boolean' },
  timer:            { label: 'Minuteur',                type: 'boolean' },
  dual_flush:       { label: 'Double chasse',           type: 'boolean' },
  built_in:         { label: 'Encastré',                type: 'boolean' },
  is_water_tank:    { label: "Réservoir d'eau",         type: 'boolean' },
  reference:        { label: 'Référence',               type: 'text' },
  general_comment:  { label: 'Commentaire',             type: 'textarea' },
};

const translateEquipmentType = (type) => EQUIPMENT_META[type]?.label || type;

// Types de machine : référentiel app_metadata (écran /admin/machine-types),
// la liste statique de FIELD_CONFIG ne sert que tant qu'il n'est pas chargé.
const fieldOptions = (key, machineTypes) =>
  key === 'type_machine' && machineTypes?.length ? machineTypes : FIELD_CONFIG[key]?.options;

const formatFieldValue = (key, value, td, machineTypes) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  const cfg = FIELD_CONFIG[key];
  if (!cfg) return String(value);
  if (cfg.type === 'select') return fieldOptions(key, machineTypes)?.find(o => o.value === String(value))?.label || String(value);
  if (cfg.unitField && td?.[cfg.unitField]) return `${value} ${td[cfg.unitField]}`;
  return String(value);
};

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */

const ChevronIcon = ({ open }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
    style={{ flexShrink: 0, transition: 'transform 250ms', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', color: 'var(--color-on-surface-var)' }}>
    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 7v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="8" cy="5" r="0.75" fill="currentColor" />
  </svg>
);

const Checkbox = ({ checked, onChange, size = 18 }) => (
  <span
    className={`ceq-checkbox ${checked ? 'ceq-checkbox--checked' : ''}`}
    style={{ width: size, height: size }}
    onClick={e => { e.stopPropagation(); onChange(!checked); }}
    role="checkbox"
    aria-checked={checked}
    tabIndex={0}
    onKeyDown={e => e.key === ' ' && onChange(!checked)}
  >
    {checked && (
      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
        <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
  </span>
);

const BulkFieldInput = ({ fieldKey, value, onChange, disabled, buildingId, buildingType }) => {
  const cfg = BULK_FIELD_CONFIG[fieldKey];
  if (!cfg) return null;

  if (cfg.type === 'usage_category') {
    // Tant que le champ n'est pas activé pour l'édition, on affiche juste le
    // type_usage courant (chaîne) — le picker (catégorie/sous-catégorie) ne
    // s'active que quand l'utilisateur choisit explicitement de le modifier.
    if (disabled) {
      return <input type="text" className="ceq-field-input" value={typeof value === 'string' ? value : ''} disabled />;
    }
    const current = value || {};
    return (
      <UsageCategoryPicker
        categoryId={current.categoryId}
        subcategoryId={current.subcategoryId}
        buildingId={buildingId}
        buildingType={buildingType}
        onChange={(categoryId, subcategoryId, label) => onChange({ categoryId, subcategoryId, label })}
      />
    );
  }
  if (cfg.type === 'boolean') {
    return (
      <div className={`ceq-toggle-row ${disabled ? 'ceq-toggle-row--disabled' : ''}`}>
        <button type="button"
          className={`ceq-toggle-track ${value ? 'ceq-toggle-track--on' : 'ceq-toggle-track--off'}`}
          onClick={() => !disabled && onChange(!value)} disabled={disabled}>
          <span className="ceq-toggle-thumb" />
        </button>
        <span className="ceq-toggle-value">{value ? 'Oui' : 'Non'}</span>
      </div>
    );
  }
  if (cfg.type === 'number') {
    return (
      <input type="number" className="ceq-field-input" value={value ?? ''} disabled={disabled}
        min={cfg.min} max={cfg.max}
        onChange={e => onChange(e.target.value === '' ? 0 : Number(e.target.value))} />
    );
  }
  if (cfg.type === 'select') {
    return (
      <select className="ceq-field-select" value={value ?? ''} disabled={disabled}
        onChange={e => onChange(e.target.value)}>
        {cfg.options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    );
  }
  return (
    <input type="text" className="ceq-field-input" value={value ?? ''} disabled={disabled}
      onChange={e => onChange(e.target.value)} />
  );
};

/* ── Editable input for an equipment's technical_data field (equipment info modal) ── */
const EquipmentFieldInput = ({ fieldKey, value, onChange, machineTypes }) => {
  const cfg = FIELD_CONFIG[fieldKey];
  if (!cfg) return null;

  if (cfg.type === 'boolean') {
    return (
      <button type="button"
        className={`cus-switch${value ? ' cus-switch--on' : ''}`}
        onClick={() => onChange(!value)}
        aria-label={cfg.label}>
        <span className="cus-switch-thumb" />
      </button>
    );
  }
  if (cfg.type === 'select') {
    return (
      <select className="cus-select" value={value ?? ''} onChange={e => onChange(e.target.value)}>
        <option value="">— Sélectionner —</option>
        {fieldOptions(fieldKey, machineTypes).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    );
  }
  if (cfg.type === 'number') {
    return (
      <input type="number" className="cus-text-input cus-text-input--compact" value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))} />
    );
  }
  if (cfg.type === 'textarea') {
    return (
      <textarea className="cus-text-input" rows={3} value={value ?? ''}
        onChange={e => onChange(e.target.value)} />
    );
  }
  return (
    <input type="text" className="cus-text-input" value={value ?? ''}
      onChange={e => onChange(e.target.value)} />
  );
};

const GAP_STATUS_META = {
  ok:        { color: '#00bf63', label: 'Écart maîtrisé' },
  attention: { color: '#f6cc27', label: 'Écart à surveiller' },
  warning:   { color: '#fc7c2c', label: 'Écart important' },
  critical:  { color: '#ff1b3b', label: 'Écart critique' },
};

const getGapStatus = (gapPercent) => {
  const abs = Math.abs(gapPercent);
  if (abs <= 5)  return 'ok';
  if (abs <= 10) return 'attention';
  if (abs <= 15) return 'warning';
  return 'critical';
};

/* ── Real vs. estimated consumption comparison ── */
const ConsumptionCompareCard = ({ realTotal, estimatedTotal, nested }) => {
  const hasReal = typeof realTotal === 'number' && realTotal > 0;

  const gapPercent = hasReal ? ((realTotal - estimatedTotal) / realTotal) * 100 : null;
  const statusMeta = gapPercent !== null ? GAP_STATUS_META[getGapStatus(gapPercent)] : null;

  const maxVal = Math.max(hasReal ? realTotal : 0, estimatedTotal, 1);
  const realBarPct = hasReal ? (realTotal / maxVal) * 100 : 0;
  const estimatedBarPct = (estimatedTotal / maxVal) * 100;

  return (
    <div className={`cus-compare-card${nested ? ' cus-compare-card--nested' : ''}`}>
      <div className="cus-compare-header">
        <div className="ceq-filter-section-title">
          <span className="ceq-filter-accent-bar" />
          <span className="ceq-filter-title-text">Réel vs estimé</span>
        </div>
        {statusMeta && (
          <span className="cus-compare-badge" style={{ '--badge-color': statusMeta.color }}>
            <span className="cus-compare-badge-dot" />
            {statusMeta.label} · {gapPercent > 0 ? '+' : ''}{gapPercent.toFixed(0)} %
          </span>
        )}
      </div>

      <div className="cus-compare-values">
        <div className="cus-compare-value-item">
          <span className="cus-compare-dot" style={{ background: 'var(--color-secondary-light)' }} />
          <div className="cus-compare-value-text">
            <span className="cus-stat-label">Consommation réelle</span>
            <span className="cus-stat-value">
              {hasReal ? Number(realTotal).toFixed(0) : '—'}
              <span className="cus-stat-unit"> m³/an</span>
            </span>
          </div>
        </div>
        <div className="cus-compare-value-item">
          <span className="cus-compare-dot" style={{ background: 'var(--color-primary)' }} />
          <div className="cus-compare-value-text">
            <span className="cus-stat-label">Consommation estimée</span>
            <span className="cus-stat-value">
              {Number(estimatedTotal).toFixed(0)}
              <span className="cus-stat-unit"> m³/an</span>
            </span>
          </div>
        </div>
      </div>

      {hasReal ? (
        <div className="cus-compare-bars">
          <div className="cus-compare-bar-row">
            <span className="cus-compare-bar-tag">Réel</span>
            <div className="cus-compare-bar-track">
              <div className="cus-compare-bar-fill cus-compare-bar-fill--real" style={{ width: `${realBarPct}%` }} />
            </div>
            <span className="cus-compare-bar-value">{Number(realTotal).toFixed(0)} m³</span>
          </div>
          <div className="cus-compare-bar-row">
            <span className="cus-compare-bar-tag">Estimé</span>
            <div className="cus-compare-bar-track">
              <div className="cus-compare-bar-fill cus-compare-bar-fill--estimated" style={{ width: `${estimatedBarPct}%` }} />
            </div>
            <span className="cus-compare-bar-value">{Number(estimatedTotal).toFixed(0)} m³</span>
          </div>
        </div>
      ) : (
        <p className="cus-compare-empty">
          Consommation réelle non renseignée pour ce bâtiment — ajoutez la donnée de compteur/facture dans les informations du bâtiment pour activer la comparaison.
        </p>
      )}
    </div>
  );
};

/* ═════════════════════════════════════════════
   Main component
═════════════════════════════════════════════ */

const ClientUsages = () => {
  const navigate = useNavigate();
  const { building_id } = useParams();
  const { data: machineTypes } = useAppMetadata('machine_types');

  // ── Data ──
  const [dataUsages, setDataUsages]           = useState([]);
  const [dataEquipments, setDataEquipments]   = useState([]);
  const [dataRooms, setDataRooms]             = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);
  const [clientId, setClientId]               = useState(null);

  // ── Filter state ──
  const [selectedSections, setSelectedSections]             = useState([]);
  const [selectedRooms, setSelectedRooms]                   = useState([]);
  const [selectedEquipmentTypes, setSelectedEquipmentTypes] = useState([]);
  const [showAllRooms, setShowAllRooms]                     = useState(false);
  const ROOMS_VISIBLE = 6;

  // ── Open equipment rows ──
  const [openEquipments, setOpenEquipments] = useState({});

  // ── Building data (functional units, users) ──
  const [buildingData, setBuildingData] = useState(null);

  // ── Real vs. estimated consumption ──
  const [compareOpen, setCompareOpen]           = useState(false);
  const [showRealWeighted, setShowRealWeighted] = useState(false);

  // ── Filters (collapsed by default, integrated into the table card) ──
  const [filtersOpen, setFiltersOpen] = useState(false);

  // ── Modals ──
  const [editingUsage, setEditingUsage]           = useState(null);
  const [isCreatingUsage, setIsCreatingUsage]     = useState(false);
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  const [showIntenseWeeks, setShowIntenseWeeks]     = useState(false);
  const [editingHotWater, setEditingHotWater]       = useState(false);
  const [saving, setSaving]                       = useState(false);
  const [equipmentInfoId, setEquipmentInfoId]     = useState(null);
  const [equipmentEditMode, setEquipmentEditMode]   = useState(false);
  const [equipmentEditData, setEquipmentEditData]   = useState(null);
  const [equipmentSaving, setEquipmentSaving]       = useState(false);
  const [equipmentSaveError, setEquipmentSaveError] = useState(null);
  // Échelle de saisie pour un point d'eau regroupant plusieurs équipements (technical_data.nb_equipment > 1) —
  // même logique que UsageForm.js (audit) : nb_users/daily_usage restent toujours stockés à l'échelle du
  // point d'eau entier (aucun facteur nb_equipment dans le calcul de volume), ce booléen ne fait que
  // diviser/multiplier l'affichage et la saisie par nb_equipment pour plus de confort.
  const [singleEquipmentView, setSingleEquipmentView] = useState(false);

  // ── Delete usage(s) — confirmation required, individual or bulk ──
  const [deleteTarget, setDeleteTarget]   = useState(null); // null | { ids: string[], usages: object[], exitBulkAfter?: boolean }
  const [deleting, setDeleting]           = useState(false);
  const [deleteError, setDeleteError]     = useState(null);

  // ── Sort ──
  const [sortOrder, setSortOrder] = useState(null); // null | 'desc' | 'asc'

  // ── Bulk mode ──
  const [bulkMode, setBulkMode]             = useState(false);
  const [selectedUsageIds, setSelectedUsageIds] = useState(new Set());
  const [bulkModalOpen, setBulkModalOpen]   = useState(false);
  const [bulkChanges, setBulkChanges]       = useState({});
  const [fieldsToUpdate, setFieldsToUpdate] = useState(new Set());
  const [bulkSaving, setBulkSaving]         = useState(false);
  const [bulkMessage, setBulkMessage]       = useState(null);

  // ── Usage type filter (stacked bar) ──
  const [selectedUsageTypes, setSelectedUsageTypes] = useState([]);
  const [hoveredSeg, setHoveredSeg]                 = useState(null);

  // ── Usage distribution (répartir des usages) ──
  const [distributionMenuOpen, setDistributionMenuOpen] = useState(false);
  const [distributionModule, setDistributionModule]     = useState(null); // null | 'users'
  const [buildingSections, setBuildingSections]         = useState([]);
  const [buildingRooms, setBuildingRooms]               = useState([]);
  const [distSelectedEquipments, setDistSelectedEquipments] = useState([]);
  const [distSelectedAssignItems, setDistSelectedAssignItems] = useState([]);
  const [distLotFormOpen, setDistLotFormOpen]           = useState(false);
  const distMenuRef = useRef(null);

  /* ── Ouverture automatique du panneau de répartition (lien depuis le suivi d'audit) ── */
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('open') === 'distribution') {
      setDistributionMenuOpen(true);
      setDistributionModule('users');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /* ── Fetch ── */
  useEffect(() => {
    if (!building_id) return;

    fetch(`${process.env.REACT_APP_API_URL}/get_client_id_from_building_id/${building_id}`)
      .then(r => r.json())
      .then(id => setClientId(id))
      .catch(() => {});

    fetch(`${process.env.REACT_APP_API_URL}/get_building/${building_id}`)
      .then(r => r.json())
      .then(setBuildingData)
      .catch(() => {});

    Promise.all([
      fetch(`${process.env.REACT_APP_API_URL}/load_usages_by_building_id/${building_id}`).then(r => r.json()),
      fetch(`${process.env.REACT_APP_API_URL}/load_equipments_with_rating/${building_id}`).then(r => r.json()),
      fetch(`${process.env.REACT_APP_API_URL}/get_rooms_from_building_id/${building_id}`).then(r => r.json()),
    ])
      .then(([usages, equipments, rooms]) => {
        setDataUsages(usages);
        setDataEquipments(equipments);
        setDataRooms(rooms);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [building_id]);

  /* ── Reload usages (used after a distribution) ── */
  const reloadUsages = useCallback(async () => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/load_usages_by_building_id/${building_id}`);
    if (res.ok) setDataUsages(await res.json());
  }, [building_id]);

  /* ── Lazy-load sections & rooms when the "users" distribution module opens ── */
  useEffect(() => {
    if (distributionModule !== 'users' || !building_id) return;
    fetch(`${process.env.REACT_APP_API_URL}/get_sections_from_building_id/${building_id}`)
      .then(r => r.json()).then(setBuildingSections).catch(() => {});
    fetch(`${process.env.REACT_APP_API_URL}/get_rooms_from_building_id/${building_id}`)
      .then(r => r.json()).then(setBuildingRooms).catch(() => {});
  }, [distributionModule, building_id]);

  /* ── Close the distribution menu on outside click ── */
  useEffect(() => {
    if (!distributionMenuOpen) return;
    const handleClickOutside = (e) => {
      if (distMenuRef.current && !distMenuRef.current.contains(e.target)) {
        setDistributionMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [distributionMenuOpen]);

  const closeDistributionPanel = useCallback(() => {
    setDistributionModule(null);
    setDistSelectedEquipments([]);
    setDistSelectedAssignItems([]);
    setDistLotFormOpen(false);
  }, []);

  /* ── Equipment map for info modal ── */
  const equipmentMap = useMemo(() => {
    const map = {};
    dataEquipments.forEach(e => { map[e._id] = e; });
    return map;
  }, [dataEquipments]);

  /* ── Room map — l'unité fonctionnelle vit sur la pièce, pas sur l'équipement,
     donc buildNewUsage doit la lire ici pour la pré-remplir par défaut. ── */
  const roomsById = useMemo(() => {
    const map = {};
    dataRooms.forEach(r => { map[r._id] = r; });
    return map;
  }, [dataRooms]);

  /* ── Enrich usages with computed annual_volume (m³) ── */
  const enrichedUsages = useMemo(() => {
    if (dataEquipments.length === 0) return dataUsages;
    return dataUsages.map(u => {
      const eq = equipmentMap[u._id_equipement];
      const computed = computeAnnualVolumeM3(eq, u);
      return { ...u, annual_volume: computed };
    });
  }, [dataUsages, equipmentMap]);

  /* ── Whole-building estimated total (unfiltered), for the real/estimated comparison ── */
  const buildingEstimatedTotal = useMemo(() =>
    enrichedUsages.reduce((s, u) => s + (u.annual_volume || 0), 0),
    [enrichedUsages]
  );

  const realTotal = getReferenceConsumption(buildingData?.global_water_data);
  const hasReal   = typeof realTotal === 'number' && realTotal > 0;

  /* ── Ratio applied to each usage's modeled volume so their sum matches the real total ── */
  const realRatio = useMemo(() =>
    (hasReal && buildingEstimatedTotal > 0) ? realTotal / buildingEstimatedTotal : 1,
    [hasReal, realTotal, buildingEstimatedTotal]
  );

  /* ── Usages used for display: raw model, or weighted to the real consumption ── */
  const displayUsages = useMemo(() =>
    showRealWeighted
      ? enrichedUsages.map(u => ({ ...u, annual_volume: (u.annual_volume || 0) * realRatio }))
      : enrichedUsages,
    [enrichedUsages, showRealWeighted, realRatio]
  );

  /* ── Deterministic color per type_usage string ── */
  const usageTypeColors = useMemo(() => {
    const types = [...new Set(enrichedUsages.map(u => u.type_usage).filter(Boolean))];
    const map = {};
    types.forEach((t, i) => { map[t] = USAGE_PALETTE[i % USAGE_PALETTE.length]; });
    return map;
  }, [enrichedUsages]);

  /* ── Filter derived data (usages + equipements sans usage, pour pouvoir filtrer jusqu'à eux) ── */
  const allSections = useMemo(() =>
    [...new Set([
      ...enrichedUsages.map(u => u.name_section),
      ...dataEquipments.map(eq => eq.location?.name_section),
    ].filter(Boolean))].sort(),
    [enrichedUsages, dataEquipments]
  );

  const availableRooms = useMemo(() =>
    [...new Set([
      ...enrichedUsages
        .filter(u => selectedSections.length === 0 || selectedSections.includes(u.name_section))
        .map(u => u.name_room),
      ...dataEquipments
        .filter(eq => selectedSections.length === 0 || selectedSections.includes(eq.location?.name_section))
        .map(eq => eq.location?.name_room),
    ].filter(Boolean))].sort(),
    [enrichedUsages, dataEquipments, selectedSections]
  );

  const presentEquipmentTypes = useMemo(() =>
    [...new Set([
      ...enrichedUsages.map(u => u.equipment_type),
      ...dataEquipments.map(eq => eq.type_equipment),
    ].filter(Boolean))],
    [enrichedUsages, dataEquipments]
  );

  useEffect(() => {
    setShowAllRooms(false);
    if (selectedSections.length === 0) return;
    const valid = new Set(availableRooms);
    setSelectedRooms(prev => prev.filter(r => valid.has(r)));
  }, [selectedSections, availableRooms]);

  /* ── Filtered usages (from displayUsages, so the real/estimated toggle applies to the whole table) ── */
  const baseFilteredUsages = useMemo(() =>
    displayUsages.filter(u =>
      (selectedSections.length === 0       || selectedSections.includes(u.name_section)) &&
      (selectedRooms.length === 0          || selectedRooms.includes(u.name_room)) &&
      (selectedEquipmentTypes.length === 0 || selectedEquipmentTypes.includes(u.equipment_type))
    ),
    [displayUsages, selectedSections, selectedRooms, selectedEquipmentTypes]
  );

  const filteredUsages = useMemo(() =>
    selectedUsageTypes.length === 0
      ? baseFilteredUsages
      : baseFilteredUsages.filter(u => selectedUsageTypes.includes(u.type_usage)),
    [baseFilteredUsages, selectedUsageTypes]
  );

  /* ── Bar stats (before usage type filter, so bar stays always visible) ── */
  const barStats = useMemo(() => {
    const total = baseFilteredUsages.reduce((s, u) => s + (u.annual_volume || 0), 0);
    const byType = {};
    baseFilteredUsages.forEach(u => {
      if (!u.type_usage) return;
      byType[u.type_usage] = (byType[u.type_usage] || 0) + (u.annual_volume || 0);
    });
    return { total, byType: Object.entries(byType).sort((a, b) => b[1] - a[1]) };
  }, [baseFilteredUsages]);

  /* ── Summary stats ── */
  const summaryStats = useMemo(() => {
    const total = filteredUsages.reduce((s, u) => s + (u.annual_volume || 0), 0);
    const byType = {};
    filteredUsages.forEach(u => {
      if (!u.type_usage) return;
      byType[u.type_usage] = (byType[u.type_usage] || 0) + (u.annual_volume || 0);
    });
    const equipmentCount = new Set(filteredUsages.map(u => u._id_equipement).filter(Boolean)).size;
    return {
      total,
      byType: Object.entries(byType).sort((a, b) => b[1] - a[1]),
      usageCount: filteredUsages.length,
      equipmentCount,
    };
  }, [filteredUsages]);

  /* ── Équipements sans aucun usage (toutes sections confondues) — on les signale dans la liste
     pour pouvoir y ajouter un premier usage, plutôt que de les rendre invisibles sur cette page. ── */
  const equipmentIdsWithUsage = useMemo(() =>
    new Set(dataUsages.map(u => u._id_equipement).filter(Boolean)),
    [dataUsages]
  );

  const orphanEquipments = useMemo(() =>
    dataEquipments.filter(eq => !equipmentIdsWithUsage.has(eq._id)),
    [dataEquipments, equipmentIdsWithUsage]
  );

  /* Le filtre "type d'usage" (barre empilée) catégorise des usages existants : un équipement
     sans usage ne peut correspondre à aucun segment, on le masque donc uniquement dans ce cas. */
  const filteredOrphanEquipments = useMemo(() => {
    if (selectedUsageTypes.length > 0) return [];
    return orphanEquipments.filter(eq =>
      (selectedSections.length === 0       || selectedSections.includes(eq.location?.name_section)) &&
      (selectedRooms.length === 0          || selectedRooms.includes(eq.location?.name_room)) &&
      (selectedEquipmentTypes.length === 0 || selectedEquipmentTypes.includes(eq.type_equipment))
    );
  }, [orphanEquipments, selectedSections, selectedRooms, selectedEquipmentTypes, selectedUsageTypes]);

  /* ── Flat equipment list (usages recensés + équipements sans usage, signalés) ── */
  const equipmentList = useMemo(() => {
    const byEq = {};
    filteredUsages.forEach(u => {
      const id = u._id_equipement || `__unknown__${u._id}`;
      if (!byEq[id]) {
        byEq[id] = { id, equipment_type: u.equipment_type, name_section: u.name_section, name_room: u.name_room, usages: [] };
      }
      byEq[id].usages.push(u);
    });
    filteredOrphanEquipments.forEach(eq => {
      if (byEq[eq._id]) return;
      byEq[eq._id] = {
        id: eq._id,
        equipment_type: eq.type_equipment,
        name_section: eq.location?.name_section || '',
        name_room: eq.location?.name_room || '',
        usages: [],
        hasNoUsage: true,
      };
    });
    const list = Object.values(byEq);
    if (sortOrder === 'desc') {
      return list.sort((a, b) =>
        b.usages.reduce((s, u) => s + (u.annual_volume || 0), 0) -
        a.usages.reduce((s, u) => s + (u.annual_volume || 0), 0)
      );
    }
    if (sortOrder === 'asc') {
      return list.sort((a, b) =>
        a.usages.reduce((s, u) => s + (u.annual_volume || 0), 0) -
        b.usages.reduce((s, u) => s + (u.annual_volume || 0), 0)
      );
    }
    return list.sort((a, b) =>
      `${a.name_section}${a.name_room}`.localeCompare(`${b.name_section}${b.name_room}`)
    );
  }, [filteredUsages, filteredOrphanEquipments, sortOrder]);

  /* ── Bulk analysis ── */
  const selectedUsageItems = useMemo(() =>
    enrichedUsages.filter(u => selectedUsageIds.has(u._id)),
    [enrichedUsages, selectedUsageIds]
  );

  const bulkAnalysis = useMemo(() => {
    if (selectedUsageItems.length === 0) return { fieldSummary: {} };
    const fieldSummary = {};
    Object.keys(BULK_FIELD_CONFIG).forEach(key => {
      const valueCounts = getBulkFieldValueCounts(key, selectedUsageItems);
      fieldSummary[key] = {
        allSame: valueCounts.length === 1,
        currentValue: valueCounts.length === 1 ? valueCounts[0].value : null,
        valueCounts,
      };
    });
    return { fieldSummary };
  }, [selectedUsageItems]);

  const usageSelectionBreakdown = useMemo(() => {
    const byType = {};
    selectedUsageItems.forEach(u => { byType[u.type_usage || '—'] = (byType[u.type_usage || '—'] || 0) + 1; });
    return byType;
  }, [selectedUsageItems]);

  /* ── New usage factory ── */
  const buildNewUsage = useCallback((eq) => {
    const type = eq.equipment_type;
    const eqDoc = equipmentMap[eq.id];
    const room = roomsById[eqDoc?.location?.room_id];
    // usage_category_id/usage_subcategory_id doivent rester synchronisés avec
    // DIC_USAGE_TAXONOMY_BY_EQUIPMENT (api/utils.py) et constants.js. "machine"
    // n'a pas de valeur par défaut fiable (cf. commentaires côté back/audit) :
    // un lave-linge et un lave-vaisselle relèvent de catégories différentes.
    const typeDefaults = {
      wc:      { type_usage: 'Sanitaires',       usage_category_id: 'tertiaire', usage_subcategory_id: 'tertiaire--sanitaires',       daily_usage: 1.72, daily_usage_type: 'per_person' },
      faucet:  { type_usage: 'Hygiène',           usage_category_id: 'tertiaire', usage_subcategory_id: 'tertiaire--lavage_des_mains', daily_usage: 1.32, daily_usage_type: 'per_person' },
      shower:  { type_usage: 'Douche',           usage_category_id: 'tertiaire', usage_subcategory_id: 'tertiaire--douche',           daily_usage: 4.04, daily_usage_type: 'per_person' },
      machine: { type_usage: '',                 usage_category_id: null,        usage_subcategory_id: null,                         daily_usage: 0,    daily_usage_type: 'total'      },
      urinal:  { type_usage: 'Sanitaires',       usage_category_id: 'tertiaire', usage_subcategory_id: 'tertiaire--sanitaires',       daily_usage: 1.34, daily_usage_type: 'per_person' },
    };
    const d = typeDefaults[type] || { type_usage: '', usage_category_id: null, usage_subcategory_id: null, daily_usage: 1, daily_usage_type: 'per_person' };
    return {
      _id: uuidv4(),
      _id_equipement: eq.id,
      type_usage: d.type_usage,
      usage_category_id: d.usage_category_id,
      usage_subcategory_id: d.usage_subcategory_id,
      functional_unit: room?.functional_unit || '',
      possible_savings: true,
      is_known_volume: false,
      annual_volume: null,
      water_quality_needed: 'drinkable',
      water_quality_out: 'non_drinkable',
      user_name: 'none',
      // display-only fields (not stored)
      name_section: eq.name_section || '',
      name_room: eq.name_room || '',
      equipment_type: type,
      usage_data: {
        nb_users: 10,
        weekly_usage: 5,
        yearly_usage: 47,
        more_intense_weeks: 0,
        less_intense_weeks: 0,
        daily_usage: d.daily_usage,
        daily_usage_type: d.daily_usage_type,
      },
    };
  }, [equipmentMap, roomsById]);

  /* ── Handlers ── */
  const toggleChip = (setter, value) =>
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);

  const resetFilters = () => {
    setSelectedSections([]); setSelectedRooms([]); setSelectedEquipmentTypes([]); setSelectedUsageTypes([]);
  };

  const toggleEquipment = id =>
    setOpenEquipments(prev => ({ ...prev, [id]: !prev[id] }));

  const openEquipmentInfo = (e, id) => {
    e.stopPropagation();
    setEquipmentInfoId(id);
    setEquipmentEditMode(false);
    setEquipmentEditData(null);
    setEquipmentSaveError(null);
  };

  const closeEquipmentInfo = () => {
    setEquipmentInfoId(null);
    setEquipmentEditMode(false);
    setEquipmentEditData(null);
    setEquipmentSaveError(null);
  };

  const startEditEquipment = () => {
    const eq = equipmentInfoId ? equipmentMap[equipmentInfoId] : null;
    if (!eq) return;
    setEquipmentEditData({ ...(eq.technical_data || {}) });
    setEquipmentEditMode(true);
    setEquipmentSaveError(null);
  };

  const cancelEditEquipment = () => {
    setEquipmentEditMode(false);
    setEquipmentEditData(null);
    setEquipmentSaveError(null);
  };

  const patchEquipmentField = (key, val) =>
    setEquipmentEditData(prev => ({ ...prev, [key]: val }));

  const saveEquipmentEdit = async () => {
    if (!equipmentInfoId || !equipmentEditData) return;
    setEquipmentSaving(true);
    setEquipmentSaveError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/update_equipment/${equipmentInfoId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ technical_data: equipmentEditData }),
      });
      if (!res.ok) throw new Error('Erreur serveur');
      setDataEquipments(prev => prev.map(e =>
        e._id === equipmentInfoId ? { ...e, technical_data: equipmentEditData } : e
      ));
      setEquipmentEditMode(false);
      setEquipmentEditData(null);
    } catch (err) {
      setEquipmentSaveError(err.message);
    } finally {
      setEquipmentSaving(false);
    }
  };

  /* ── Edit / create usage handlers ── */
  const handleOpenEdit = useCallback((usage) => {
    const raw = dataUsages.find(u => u._id === usage._id) || usage;
    setIsCreatingUsage(false);
    setEditingUsage({ ...raw });
    setSingleEquipmentView(false);
    // Repliés par défaut à chaque ouverture, sauf si l'usage a déjà des semaines
    // intenses/réduites renseignées — dans ce cas on les laisse visibles plutôt
    // que de masquer une donnée déjà saisie.
    setShowIntenseWeeks(!!(raw.usage_data?.more_intense_weeks || raw.usage_data?.less_intense_weeks));
    setEditingHotWater(false);
  }, [dataUsages]);

  const handleOpenCreate = useCallback((e, eq) => {
    e.stopPropagation();
    setIsCreatingUsage(true);
    setEditingUsage(buildNewUsage(eq));
    setSingleEquipmentView(false);
    setShowIntenseWeeks(false);
    setEditingHotWater(false);
  }, [buildNewUsage]);

  const handleCancelEdit = useCallback(() => { setEditingUsage(null); setIsCreatingUsage(false); }, []);

  const patch = useCallback((key, val) =>
    setEditingUsage(prev => ({ ...prev, [key]: val })), []);

  const patchData = useCallback((key, val) =>
    setEditingUsage(prev => ({
      ...prev,
      usage_data: { ...(prev.usage_data || {}), [key]: val }
    })), []);

  const handleSaveUsage = async () => {
    if (!editingUsage) return;
    setSaving(true);
    const { name_building, name_section, name_room, equipment_type, machine_type, ...sendUsage } = editingUsage;
    if (!sendUsage.is_known_volume) sendUsage.annual_volume = null;

    try {
      if (isCreatingUsage) {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/load_new_usages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ usages: [sendUsage] }),
        });
        if (!res.ok) throw new Error('Erreur serveur');
        // Re-attach display-only fields so the list updates immediately
        setDataUsages(prev => [...prev, {
          ...sendUsage,
          name_section: editingUsage.name_section,
          name_room: editingUsage.name_room,
          equipment_type: editingUsage.equipment_type,
        }]);
      } else {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/update_usage/${editingUsage._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sendUsage),
        });
        if (!res.ok) throw new Error('Erreur serveur');
        setDataUsages(prev => prev.map(u =>
          u._id === editingUsage._id ? { ...u, ...sendUsage } : u
        ));
      }
      setEditingUsage(null);
      setIsCreatingUsage(false);
    } catch (err) {
      alert('Erreur lors de la sauvegarde : ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  /* ── Bulk mode handlers ── */
  const toggleUsageSelect = useCallback((id, e) => {
    e?.stopPropagation();
    setSelectedUsageIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  const selectAllUsagesOfEquipment = useCallback((eq) => {
    const allSelected = eq.usages.every(u => selectedUsageIds.has(u._id));
    setSelectedUsageIds(prev => {
      const n = new Set(prev);
      eq.usages.forEach(u => allSelected ? n.delete(u._id) : n.add(u._id));
      return n;
    });
  }, [selectedUsageIds]);

  const enterBulkMode = () => {
    setBulkMode(true);
    setOpenEquipments(prev => {
      const n = { ...prev };
      equipmentList.forEach(eq => { n[eq.id] = true; });
      return n;
    });
  };

  const exitBulkMode = () => { setBulkMode(false); setSelectedUsageIds(new Set()); };

  const selectAllFilteredUsages = () => {
    setSelectedUsageIds(new Set(filteredUsages.map(u => u._id)));
  };

  /* ── Delete usage(s) handlers ── */
  const requestDeleteUsage = (e, usage) => {
    e.stopPropagation();
    setDeleteTarget({ ids: [usage._id], usages: [usage] });
    setDeleteError(null);
  };

  const requestDeleteEditing = () => {
    if (!editingUsage || isCreatingUsage) return;
    setDeleteTarget({ ids: [editingUsage._id], usages: [editingUsage] });
    setDeleteError(null);
  };

  const requestDeleteSelected = () => {
    if (selectedUsageIds.size === 0) return;
    setDeleteTarget({ ids: [...selectedUsageIds], usages: selectedUsageItems, exitBulkAfter: true });
    setDeleteError(null);
  };

  const cancelDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/delete_usage_list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usage_ids: deleteTarget.ids }),
      });
      if (!res.ok) throw new Error('Erreur serveur');
      const deletedIds = new Set(deleteTarget.ids);
      setDataUsages(prev => prev.filter(u => !deletedIds.has(u._id)));
      if (editingUsage && deletedIds.has(editingUsage._id)) {
        setEditingUsage(null);
        setIsCreatingUsage(false);
      }
      if (deleteTarget.exitBulkAfter) exitBulkMode();
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const openBulkModal = () => {
    setBulkChanges({});
    setFieldsToUpdate(new Set());
    setBulkMessage(null);
    setBulkModalOpen(true);
  };

  const toggleFieldToUpdate = (key) => {
    if (!fieldsToUpdate.has(key)) {
      if (key === 'type_usage') {
        // Le picker catégorie/sous-catégorie démarre toujours vierge : la valeur
        // actuelle (chaîne type_usage) ne peut pas être traduite en ids sans
        // ambiguïté, on force donc un choix explicite plutôt que de deviner.
        setBulkChanges(prev => ({ ...prev, type_usage: { categoryId: null, subcategoryId: null, label: null } }));
      } else {
        const summary = bulkAnalysis.fieldSummary[key];
        if (summary) {
          const initVal = summary.allSame
            ? summary.currentValue
            : summary.valueCounts.sort((a, b) => b.count - a.count)[0]?.value;
          setBulkChanges(prev => ({ ...prev, [key]: initVal }));
        }
      }
    }
    setFieldsToUpdate(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const saveBulkChanges = async () => {
    if (fieldsToUpdate.size === 0) return;
    setBulkSaving(true);
    const usageUpdates = {};
    const usageDataUpdates = {};
    fieldsToUpdate.forEach(key => {
      const cfg = BULK_FIELD_CONFIG[key];
      if (key === 'type_usage') {
        const { categoryId, subcategoryId, label } = bulkChanges.type_usage || {};
        if (categoryId) usageUpdates.usage_category_id = categoryId;
        if (subcategoryId) usageUpdates.usage_subcategory_id = subcategoryId;
        // Envoyé pour que la mise à jour optimiste locale (ci-dessous) reste
        // immédiatement cohérente ; le serveur re-dérive de toute façon la
        // même valeur depuis usage_subcategory_id à l'écriture.
        if (label) usageUpdates.type_usage = label;
      } else if (cfg.group === 'data') {
        usageDataUpdates[key] = bulkChanges[key];
      } else {
        usageUpdates[key] = bulkChanges[key];
      }
    });
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/update_usages_bulk`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [...selectedUsageIds], usage_updates: usageUpdates, usage_data_updates: usageDataUpdates }),
      });
      if (!res.ok) throw new Error('Erreur lors de la sauvegarde groupée');
      setDataUsages(prev => prev.map(u => {
        if (!selectedUsageIds.has(u._id)) return u;
        return { ...u, ...usageUpdates, usage_data: { ...u.usage_data, ...usageDataUpdates } };
      }));
      setBulkMessage({ type: 'success', text: `${selectedUsageIds.size} usage(s) mis à jour` });
      setTimeout(() => { setBulkModalOpen(false); exitBulkMode(); }, 1600);
    } catch (err) {
      setBulkMessage({ type: 'error', text: err.message });
    } finally {
      setBulkSaving(false);
    }
  };

  const hasActiveFilters = selectedSections.length > 0 || selectedRooms.length > 0 || selectedEquipmentTypes.length > 0 || selectedUsageTypes.length > 0;
  const activeCount = selectedSections.length + selectedRooms.length + selectedEquipmentTypes.length + selectedUsageTypes.length;

  const infoEquipment = equipmentInfoId ? equipmentMap[equipmentInfoId] : null;

  /* ── Guards ── */
  if (loading) return <div className="centered-spinner-dashboard"><WalterLoader /></div>;
  if (error)   return <p style={{ padding: 32, color: '#dc2626' }}>Erreur : {error}</p>;

  /* ── Edit modal computed values ── */
  const editEq     = editingUsage ? equipmentMap[editingUsage._id_equipement] : null;
  const editEqMeta = editingUsage ? (EQUIPMENT_META[editingUsage.equipment_type] || { label: editingUsage.equipment_type, icon: '●' }) : null;
  const ud         = editingUsage?.usage_data || {};
  const liveVolume = editingUsage
    ? (editingUsage.is_known_volume ? (editingUsage.annual_volume || 0) : computeAnnualVolumeM3(editEq, editingUsage))
    : 0;
  const nbEquipments    = editEq?.technical_data?.nb_equipment || 1;
  const isTotalDailyUsage = (ud.daily_usage_type || 'per_person') === 'total';
  const scaleDailyUsage   = singleEquipmentView && isTotalDailyUsage && nbEquipments > 1;
  const maxIntense = Math.max(0, (ud.yearly_usage || 52) - (ud.less_intense_weeks || 0));
  const maxLess    = Math.max(0, (ud.yearly_usage || 52) - (ud.more_intense_weeks || 0));
  const dailyLabel = editingUsage ? (DAILY_USAGE_LABELS[editingUsage.equipment_type] || 'Utilisations / jour') : '';
  const annualLabel = editingUsage ? (ANNUAL_USAGE_LABELS[editingUsage.equipment_type] || 'utilisations / an') : '';
  const usageStats = editingUsage ? computeAnnualActivations(ud) : null;
  // Part retenue : celle de l'usage, sinon héritée de l'équipement ; 0 si non raccordé.
  const hotWater = editingUsage
    ? getHotWaterRatioWithSource({ usage: editingUsage, equipmentType: editingUsage.equipment_type, technicalData: editEq?.technical_data })
    : { value: 0, source: 'default' };
  const hotWaterNotConnected = hotWater.source === 'not_connected';

  // Building dropdowns
  const buildingFunctionalUnits = buildingData?.functional_units || [];
  const fuMatch         = editingUsage ? buildingFunctionalUnits.find(fu => fu.id === editingUsage.functional_unit) : null;
  const isCustomFU      = !!(editingUsage?.functional_unit && !fuMatch);

  /* ══════════════════════════════════════════════ */
  return (
    <div className={`ceq-page ${bulkMode ? 'ceq-page--bulk' : ''}`}>

      {/* ── Page header ── */}
      <div className="ceq-header">
        <button type="button" className="back-button"
          onClick={() => navigate(`/client/${clientId}/${building_id}`)}>
          ← Tableau de bord
        </button>
        <h1 className="ceq-title">Usages</h1>
        <div className="ceq-header-actions">
          {bulkMode ? (
            <button type="button" className="ceq-bulk-mode-btn ceq-bulk-mode-btn--exit" onClick={exitBulkMode}>
              ✕ Quitter la sélection
            </button>
          ) : (
            <button type="button" className="ceq-bulk-mode-btn" onClick={enterBulkMode} disabled={!!distributionModule}>
              ☑ Modifier en bloc
            </button>
          )}
        </div>
        <div className="cus-dist-trigger-wrap" ref={distMenuRef}>
          <button type="button"
            className={`cus-dist-trigger-btn${distributionModule ? ' cus-dist-trigger-btn--active' : ''}`}
            onClick={() => distributionModule ? closeDistributionPanel() : setDistributionMenuOpen(o => !o)}
            disabled={bulkMode}>
            {distributionModule ? '✕ Fermer la répartition' : '⇄ Répartir des usages'}
          </button>

          {distributionMenuOpen && !distributionModule && (
            <div className="cus-dist-menu">
              <button type="button" className="cus-dist-menu-item"
                onClick={() => { setDistributionModule('users'); setDistributionMenuOpen(false); }}>
                Répartition des usagers
              </button>
              <button type="button" className="cus-dist-menu-item cus-dist-menu-item--disabled" disabled>
                Ménage
                <span className="cus-dist-menu-badge">Bientôt disponible</span>
              </button>
              <button type="button" className="cus-dist-menu-item cus-dist-menu-item--disabled" disabled>
                Nettoyage
                <span className="cus-dist-menu-badge">Bientôt disponible</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {distributionModule === 'users' && buildingData && (
        <UserDistributionPanel
          buildingId={building_id}
          buildingData={buildingData}
          setBuildingData={setBuildingData}
          buildingUsages={dataUsages}
          setBuildingUsages={setDataUsages}
          equipments={dataEquipments}
          sections={buildingSections}
          rooms={buildingRooms}
          selectedEquipments={distSelectedEquipments}
          setSelectedEquipments={setDistSelectedEquipments}
          selectedAssignItems={distSelectedAssignItems}
          setSelectedAssignItems={setDistSelectedAssignItems}
          translate={translateEquipmentType}
          onClose={closeDistributionPanel}
          onAssigned={reloadUsages}
          onLotFormOpenChange={setDistLotFormOpen}
        />
      )}

      {distributionModule === null && (
      <div className="cus-summary-card">
        <div className="cus-summary-header">
          <div className="ceq-filter-section-title">
            <span className="ceq-filter-accent-bar" />
            <span className="ceq-filter-title-text">Consommation de la sélection</span>
          </div>
        </div>

        <div className="cus-summary-stats">
          <div
            className={`cus-stat-box cus-stat-box--expandable${compareOpen ? ' cus-stat-box--open' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => setCompareOpen(o => !o)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCompareOpen(o => !o); } }}
          >
            <div className="cus-stat-box-top-row">
              <span className="cus-stat-label">
                Volume annuel <span className="cus-stat-label-mode">· {showRealWeighted ? 'réel' : 'modélisé'}</span>
              </span>
              <div className="cus-real-segmented" onClick={e => e.stopPropagation()} role="group" aria-label="Mode d'affichage du volume">
                <button type="button"
                  className={`cus-real-segment${!showRealWeighted ? ' cus-real-segment--active' : ''}`}
                  title="Afficher le volume modélisé par l'audit"
                  onClick={() => setShowRealWeighted(false)}
                >
                  Modélisé
                </button>
                <button type="button"
                  className={`cus-real-segment${showRealWeighted ? ' cus-real-segment--active' : ''}`}
                  disabled={!hasReal}
                  title={hasReal
                    ? 'Afficher le modèle pondéré pour que la somme des usages corresponde à la consommation réelle du bâtiment'
                    : 'Consommation réelle non renseignée pour ce bâtiment'}
                  onClick={() => setShowRealWeighted(true)}
                >
                  Réel
                </button>
              </div>
            </div>
            <span className="cus-stat-value">
              {Number(summaryStats.total).toFixed(1)}
              <span className="cus-stat-unit"> m³</span>
            </span>
            <span className="cus-stat-box-chevron"><ChevronIcon open={compareOpen} /></span>
          </div>
          <div className="cus-stat-box cus-stat-box--split">
            <div className="cus-stat-box-split-item">
              <span className="cus-stat-label">Usages recensés</span>
              <span className="cus-stat-value">{summaryStats.usageCount}</span>
            </div>
            <span className="cus-stat-box-split-divider" />
            <div className="cus-stat-box-split-item">
              <span className="cus-stat-label">Points d'eau</span>
              <span className="cus-stat-value">
                {summaryStats.equipmentCount}
                {filteredOrphanEquipments.length > 0 && (
                  <span className="cus-stat-value-hint"> +{filteredOrphanEquipments.length} sans usage</span>
                )}
              </span>
            </div>
          </div>
        </div>

        {compareOpen && (
          <ConsumptionCompareCard
            realTotal={realTotal}
            estimatedTotal={buildingEstimatedTotal}
            nested
          />
        )}

      </div>
      )}

      {/* ── Table card: filters (collapsed by default) + equipment/usage list, merged into one card ── */}
      <div className="ceq-accordion">
        {distributionModule === null && (
        <>
        <button type="button"
          className="ceq-filter-toggle-header"
          onClick={() => setFiltersOpen(o => !o)}
          aria-expanded={filtersOpen}
        >
          <div className="ceq-filter-section-title">
            <span className="ceq-filter-accent-bar" />
            <span className="ceq-filter-title-text">Filtres</span>
            {hasActiveFilters && (
              <span className="ceq-active-badge">{activeCount} actif{activeCount > 1 ? 's' : ''}</span>
            )}
          </div>
          <div className="ceq-filter-toggle-right">
            {hasActiveFilters && (
              <span
                className="ceq-reset-btn"
                role="button"
                tabIndex={0}
                onClick={e => { e.stopPropagation(); resetFilters(); }}
                onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); resetFilters(); } }}
              >
                Réinitialiser
              </span>
            )}
            <ChevronIcon open={filtersOpen} />
          </div>
        </button>

        {filtersOpen && (
        <div className="ceq-filter-body">

        {allSections.length > 0 && (
          <div className="ceq-filter-group">
            <span className="ceq-filter-label">Section</span>
            <div className="ceq-filter-chips">
              {allSections.map(s => (
                <button key={s} type="button"
                  className={`ceq-chip${selectedSections.includes(s) ? ' ceq-chip--active' : ''}`}
                  onClick={() => toggleChip(setSelectedSections, s)}>{s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="ceq-filter-group">
          <span className="ceq-filter-label">
            Salle
            {selectedSections.length > 0 && (
              <span className="ceq-filter-label-hint">
                {' '}— {selectedSections.length} section{selectedSections.length > 1 ? 's' : ''} sélectionnée{selectedSections.length > 1 ? 's' : ''}
              </span>
            )}
          </span>
          <div className="ceq-filter-chips">
            {(showAllRooms ? availableRooms : availableRooms.slice(0, ROOMS_VISIBLE)).map(r => (
              <button key={r} type="button"
                className={`ceq-chip${selectedRooms.includes(r) ? ' ceq-chip--active' : ''}`}
                onClick={() => toggleChip(setSelectedRooms, r)}>{r}
              </button>
            ))}
            {!showAllRooms && availableRooms.length > ROOMS_VISIBLE && (
              <button type="button" className="ceq-chip ceq-chip--expand" onClick={() => setShowAllRooms(true)}>
                + {availableRooms.length - ROOMS_VISIBLE} salles
              </button>
            )}
            {showAllRooms && availableRooms.length > ROOMS_VISIBLE && (
              <button type="button" className="ceq-chip ceq-chip--expand" onClick={() => setShowAllRooms(false)}>
                Réduire
              </button>
            )}
          </div>
        </div>

        {presentEquipmentTypes.length > 1 && (
          <div className="ceq-filter-group">
            <span className="ceq-filter-label">Type d'équipement</span>
            <div className="ceq-filter-chips">
              {presentEquipmentTypes.map(type => {
                const meta = EQUIPMENT_META[type] || { label: type, icon: '●' };
                return (
                  <button key={type} type="button"
                    className={`ceq-chip${selectedEquipmentTypes.includes(type) ? ' ceq-chip--active' : ''}`}
                    onClick={() => toggleChip(setSelectedEquipmentTypes, type)}>
                    {meta.icon} {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {barStats.byType.length > 0 && (
          <div className="ceq-filter-group cus-bar-filter-group">
            <div className="cus-bar-filter-header">
              <span className="ceq-filter-label">Répartition par usage</span>
              <div className="cus-bar-filter-volumes">
                {selectedUsageTypes.length > 0 ? (
                  <>
                    <span className="cus-bar-filter-vol-item">
                      <span className="cus-bar-filter-vol-label">Filtre</span>
                      <span className="cus-bar-filter-vol-value">{Number(summaryStats.total).toFixed(1)} m³/an</span>
                    </span>
                    <span className="cus-bar-filter-vol-sep">·</span>
                    <span className="cus-bar-filter-vol-item cus-bar-filter-vol-item--muted">
                      <span className="cus-bar-filter-vol-label">Total</span>
                      <span className="cus-bar-filter-vol-value">{Number(barStats.total).toFixed(1)} m³/an</span>
                    </span>
                  </>
                ) : (
                  <span className="cus-bar-filter-vol-item">
                    <span className="cus-bar-filter-vol-label">Sélection</span>
                    <span className="cus-bar-filter-vol-value">{Number(barStats.total).toFixed(1)} m³/an</span>
                  </span>
                )}
              </div>
            </div>

            <div className="cus-stacked-bar">
              {barStats.byType.map(([type, vol]) => {
                const pct = barStats.total > 0 ? (vol / barStats.total) * 100 : 0;
                const color = usageTypeColors[type] || '#94a3b8';
                const isActive = selectedUsageTypes.includes(type);
                const isDimmed = selectedUsageTypes.length > 0 && !isActive;
                return (
                  <div key={type}
                    className={`cus-stacked-seg${isActive ? ' cus-stacked-seg--active' : ''}`}
                    style={{ flex: vol, background: color, opacity: isDimmed ? 0.22 : 1 }}
                    onMouseEnter={() => setHoveredSeg(type)}
                    onMouseLeave={() => setHoveredSeg(null)}
                    onClick={() => toggleChip(setSelectedUsageTypes, type)}
                  >
                    {hoveredSeg === type && (
                      <div className="cus-seg-tooltip">
                        <span className="cus-seg-tooltip-type">{type}</span>
                        <span className="cus-seg-tooltip-vol">{Number(vol).toFixed(1)} m³</span>
                        <span className="cus-seg-tooltip-pct">{pct.toFixed(0)} %</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="cus-stacked-legend">
              {barStats.byType.map(([type]) => {
                const color = usageTypeColors[type] || '#94a3b8';
                const isActive = selectedUsageTypes.includes(type);
                return (
                  <button key={type} type="button"
                    className={`cus-legend-item${isActive ? ' cus-legend-item--active' : ''}`}
                    onClick={() => toggleChip(setSelectedUsageTypes, type)}>
                    <span className="cus-legend-dot" style={{ background: color }} />
                    {type}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        </div>
        )}
        </>
        )}

        {/* ── Flat equipment list ── */}
        {equipmentList.length > 0 ? (
          <>
          {bulkMode && (
            <div className="cus-bulk-select-bar">
              <button type="button" className="ceq-select-all-btn" onClick={selectAllFilteredUsages}>
                Tout sélectionner
              </button>
              <button type="button" className="ceq-select-all-btn" onClick={() => setSelectedUsageIds(new Set())}>
                Tout désélectionner
              </button>
            </div>
          )}
          {showRealWeighted && (
            <div className="cus-real-weighted-banner">
              <span className="cus-real-toggle-dot" />
              Volumes pondérés sur la consommation réelle du bâtiment (facteur ×{realRatio.toFixed(2)})
            </div>
          )}
          <div className="cus-list-header">
            <span>Point d'eau</span>
            <span>Usages</span>
            <button type="button" className="cus-sort-btn"
              onClick={() => setSortOrder(o => o === null ? 'desc' : o === 'desc' ? 'asc' : null)}
              title={sortOrder === 'asc' ? 'Volume croissant' : sortOrder === 'desc' ? 'Volume décroissant' : 'Trier par volume'}>
              Volume annuel
              {showRealWeighted && (
                <span className="cus-real-col-flag" title={`Volumes pondérés sur la consommation réelle (×${realRatio.toFixed(2)})`}>
                  <span className="cus-real-toggle-dot" /> réel
                </span>
              )}
              <span className="cus-sort-icon">
                {sortOrder === 'desc' ? '↓' : sortOrder === 'asc' ? '↑' : '↕'}
              </span>
            </button>
          </div>

          {equipmentList.map(eq => {
            const isOpen = !!openEquipments[eq.id];
            const eqTotal = eq.usages.reduce((s, u) => s + (u.annual_volume || 0), 0);
            const meta = EQUIPMENT_META[eq.equipment_type] || { label: eq.equipment_type, icon: '●' };
            const hasEquipmentData = !!equipmentMap[eq.id];
            // Même règle d'éligibilité que la sélection automatique par section/pièce
            // (UserDistributionPanel) : une machine (ou un robinet de cuisine, etc.)
            // n'a pas de formule de calcul par personne ni de catégorie de taxonomie
            // fiable, un usage créé pour elle serait à la fois à volume nul et non
            // catégorisé — jamais proposée ici, qu'elle soit sélectionnée à la main
            // ou automatiquement. Les cases n'apparaissent que pendant la saisie d'un lot.
            const isDistSelectable = distributionModule === 'users' && distLotFormOpen && hasEquipmentData && isEquipmentAllowed(equipmentMap[eq.id]);
            const isDistSelected = isDistSelectable && distSelectedEquipments.includes(eq.id);
            const allUsagesSelected = bulkMode && eq.usages.every(u => selectedUsageIds.has(u._id));
            const someUsagesSelected = bulkMode && eq.usages.some(u => selectedUsageIds.has(u._id));

            return (
              <div key={eq.id} className={`cus-equipment-block${isOpen ? ' cus-equipment-block--open' : ''}${isDistSelected ? ' cus-equipment-block--selected' : ''}${eq.hasNoUsage ? ' cus-equipment-block--empty' : ''}`}>
                <button type="button" className="cus-equipment-header"
                  onClick={() => toggleEquipment(eq.id)} aria-expanded={isOpen}>

                  {isDistSelectable && (
                    <input
                      type="checkbox"
                      className="cus-eq-select-checkbox"
                      checked={isDistSelected}
                      onClick={e => e.stopPropagation()}
                      onChange={() => setDistSelectedEquipments(prev =>
                        prev.includes(eq.id) ? prev.filter(id => id !== eq.id) : [...prev, eq.id]
                      )}
                    />
                  )}

                  <span className="cus-eq-type-icon" role="img" aria-label={meta.label}>{meta.icon}</span>

                  <div className="cus-equipment-location">
                    <span className="cus-breadcrumb-section">{eq.name_section || '—'}</span>
                    <span className="cus-breadcrumb-sep">›</span>
                    <span className="cus-breadcrumb-room">{eq.name_room || '—'}</span>
                    {equipmentMap[eq.id]?.location?.room_multiplier > 1 && (
                      <span
                        className="cus-room-multiplier-badge"
                        title={`Ce point d'eau est répété dans ${equipmentMap[eq.id].location.room_multiplier} pièces identiques : les volumes affichés sont déjà multipliés en conséquence.`}
                      >
                        ×{equipmentMap[eq.id].location.room_multiplier}
                      </span>
                    )}
                    <span className="cus-eq-type-badge">{meta.label}</span>
                    {bulkMode && eq.usages.length > 0 && (
                      <button type="button" className="ceq-select-all-btn"
                        onClick={e => { e.stopPropagation(); selectAllUsagesOfEquipment(eq); }}>
                        {allUsagesSelected ? 'Désélectionner tout' : someUsagesSelected ? 'Compléter la sélection' : 'Tout sélectionner'}
                      </button>
                    )}
                  </div>

                  <div className="cus-equipment-meta">
                    <div className="cus-eq-usage-tags">
                      {eq.hasNoUsage ? (
                        <span className="cus-eq-usage-tag cus-eq-usage-tag--empty">
                          ⚠ Aucun usage
                        </span>
                      ) : (
                        <>
                          {eq.usages.slice(0, 2).map(u => {
                            const color = usageTypeColors[u.type_usage] || '#94a3b8';
                            return (
                              <span key={u._id} className="cus-eq-usage-tag">
                                <span className="cus-usage-dot" style={{ background: color }} />
                                {u.type_usage || '—'}
                              </span>
                            );
                          })}
                          {eq.usages.length > 2 && (
                            <span className="cus-eq-usage-tag cus-eq-usage-tag--more">
                              +{eq.usages.length - 2}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    {hasEquipmentData && (
                      <button type="button" className="cus-info-btn"
                        onClick={e => openEquipmentInfo(e, eq.id)}
                        title="Voir les données de l'équipement">
                        <InfoIcon />
                      </button>
                    )}
                    <span className="cus-eq-vol">{eq.hasNoUsage ? '—' : `${Number(eqTotal).toFixed(1)} m³/an`}</span>
                    <ChevronIcon open={isOpen} />
                  </div>
                </button>

                {isOpen && (
                  <div className="cus-usage-list">
                    {eq.hasNoUsage && (
                      <p className="cus-eq-empty-hint">
                        Aucun usage n'a encore été renseigné pour ce point d'eau — ajoutez-en un pour l'intégrer au calcul de consommation.
                      </p>
                    )}
                    {eq.usages.map(u => {
                      const color = usageTypeColors[u.type_usage] || '#94a3b8';
                      const isUsageSelected = bulkMode && selectedUsageIds.has(u._id);
                      return (
                        <div key={u._id} className={`cus-usage-row ${isUsageSelected ? 'ceq-table-row--selected' : ''}`}
                          onClick={e => bulkMode ? toggleUsageSelect(u._id, e) : handleOpenEdit(u)}>
                          {bulkMode && (
                            <Checkbox checked={isUsageSelected} onChange={() => toggleUsageSelect(u._id)} size={16} />
                          )}
                          <span className="cus-usage-dot" style={{ background: color }} />
                          <div className="cus-usage-type-col">
                            <span className="cus-usage-type">{u.type_usage || '—'}</span>
                            {u.user_name && u.user_name !== 'none' && (
                              <span className="cus-usage-user-badge">{u.user_name}</span>
                            )}
                          </div>
                          {u.usage_data?.nb_users != null && (
                            <span className="cus-usage-chip">
                              {u.usage_data.nb_users} pers.
                            </span>
                          )}
                          {u.usage_data?.daily_usage != null && (
                            <span className="cus-usage-chip cus-usage-chip--secondary">
                              {Number(u.usage_data.daily_usage).toFixed(1)} util./j
                            </span>
                          )}
                          <span className="cus-usage-vol">
                            {Number(u.annual_volume || 0).toFixed(1)} m³/an
                          </span>
                          {!bulkMode && (
                            <button type="button" className="cus-usage-delete-btn"
                              onClick={e => requestDeleteUsage(e, u)}
                              title="Supprimer cet usage" aria-label="Supprimer cet usage">
                              🗑
                            </button>
                          )}
                          {!bulkMode && <span className="cus-usage-arrow">✎</span>}
                        </div>
                      );
                    })}
                    {!bulkMode && (
                      <button type="button" className="cus-add-usage-btn"
                        onClick={e => handleOpenCreate(e, eq)}>
                        <span className="cus-add-usage-icon">+</span>
                        Ajouter un usage
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          </>
        ) : (
          <div className="cus-empty-state">
            <p className="cus-empty-state-text">Aucun usage pour cette sélection.</p>
          </div>
        )}
      </div>

      {/* ── Bulk selection footer ── */}
      {bulkMode && selectedUsageIds.size > 0 && (
        <div className="ceq-bulk-footer">
          <div className="ceq-bulk-footer-left">
            <div className="ceq-bulk-footer-count">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                <rect x="1" y="1" width="14" height="14" rx="3" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2"/>
                <path d="M4 8l3 3 5-6" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span><strong>{selectedUsageIds.size}</strong> usage{selectedUsageIds.size > 1 ? 's' : ''} sélectionné{selectedUsageIds.size > 1 ? 's' : ''}</span>
            </div>
            <div className="ceq-bulk-footer-breakdown">
              {Object.entries(usageSelectionBreakdown).map(([type, count]) => (
                <span key={type} className="ceq-bulk-breakdown-tag">
                  {count} {type}
                </span>
              ))}
            </div>
          </div>
          <div className="ceq-bulk-footer-actions">
            <button type="button" className="ceq-bulk-footer-clear" onClick={() => setSelectedUsageIds(new Set())}>
              Tout désélectionner
            </button>
            <button type="button" className="ceq-bulk-footer-cta ceq-bulk-footer-cta--danger" onClick={requestDeleteSelected}>
              🗑 Supprimer →
            </button>
            <button type="button" className="ceq-bulk-footer-cta" onClick={openBulkModal}>
              Modifier en bloc →
            </button>
          </div>
        </div>
      )}

      {/* ── Bulk edit modal ── */}
      {bulkModalOpen && (
        <>
          <div className="ceq-modal-backdrop" onClick={() => !bulkSaving && setBulkModalOpen(false)} />
          <div className="ceq-modal ceq-modal--bulk" role="dialog" aria-modal="true">
            <div className="ceq-modal-header">
              <div>
                <p className="ceq-modal-header-label">Modification en bloc</p>
                <h2 className="ceq-modal-header-title">{selectedUsageIds.size} usage{selectedUsageIds.size > 1 ? 's' : ''} sélectionné{selectedUsageIds.size > 1 ? 's' : ''}</h2>
              </div>
              <button type="button" className="ceq-modal-close" onClick={() => !bulkSaving && setBulkModalOpen(false)}>✕</button>
            </div>

            <div className="ceq-modal-body">
              {/* Info banner */}
              <div className="ceq-bulk-info-banner">
                <InfoIcon />
                <p>Cochez un champ pour le modifier sur tous les usages sélectionnés. Les champs non cochés resteront inchangés.</p>
              </div>

              {/* Bulk fields */}
              <div className="ceq-bulk-fields">
                {Object.entries(BULK_FIELD_CONFIG).map(([key, cfg]) => {
                  const summary = bulkAnalysis.fieldSummary[key];
                  if (!summary) return null;
                  const enabled = fieldsToUpdate.has(key);
                  return (
                    <div key={key} className={`ceq-bulk-field ${enabled ? 'ceq-bulk-field--active' : ''}`}>
                      <div className="ceq-bulk-field-header">
                        <span className="ceq-field-label">{cfg.label}</span>
                        <label className="ceq-bulk-enable" onClick={() => toggleFieldToUpdate(key)}>
                          <Checkbox checked={enabled} onChange={() => toggleFieldToUpdate(key)} size={16} />
                          <span>Modifier</span>
                        </label>
                      </div>

                      <div className={`ceq-bulk-summary ${summary.allSame ? 'ceq-bulk-summary--same' : 'ceq-bulk-summary--diff'}`}>
                        {summary.allSame ? (
                          <>
                            <span className="ceq-bulk-summary-dot ceq-bulk-dot--same" />
                            <span>Identiques :&nbsp;<strong>{formatBulkFieldValue(key, summary.currentValue)}</strong></span>
                          </>
                        ) : (
                          <>
                            <span className="ceq-bulk-summary-dot ceq-bulk-dot--diff" />
                            <span>{summary.valueCounts.length} valeurs :&nbsp;</span>
                            {summary.valueCounts.sort((a, b) => b.count - a.count).map(({ value, count }) => (
                              <span key={String(value)} className="ceq-bulk-value-tag">
                                {formatBulkFieldValue(key, value)} ×{count}
                              </span>
                            ))}
                          </>
                        )}
                      </div>

                      <div className={`ceq-bulk-field-input ${!enabled ? 'ceq-bulk-field-input--off' : ''}`}>
                        <BulkFieldInput
                          fieldKey={key}
                          value={enabled ? bulkChanges[key] : (summary.allSame ? summary.currentValue : null)}
                          onChange={v => setBulkChanges(p => ({ ...p, [key]: v }))}
                          disabled={!enabled}
                          buildingId={building_id}
                          buildingType={buildingData?.type_building}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Warning */}
              {fieldsToUpdate.size > 0 && (
                <div className="ceq-bulk-warning">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                    <path d="M8 2L14.5 13.5H1.5L8 2Z" stroke="#964900" strokeWidth="1.4" strokeLinejoin="round"/>
                    <path d="M8 6.5V9" stroke="#964900" strokeWidth="1.4" strokeLinecap="round"/>
                    <circle cx="8" cy="11" r="0.75" fill="#964900"/>
                  </svg>
                  <p>
                    <strong>{fieldsToUpdate.size} champ{fieldsToUpdate.size > 1 ? 's' : ''}</strong> seront modifiés sur <strong>{selectedUsageIds.size} usage{selectedUsageIds.size > 1 ? 's' : ''}</strong>. Cette action est immédiate et remplacera les valeurs existantes.
                  </p>
                </div>
              )}

              {bulkMessage && (
                <div className={`ceq-modal-msg ceq-modal-msg--${bulkMessage.type}`}>{bulkMessage.text}</div>
              )}
            </div>

            <div className="ceq-modal-footer">
              <button type="button" className="ceq-modal-cancel" onClick={() => !bulkSaving && setBulkModalOpen(false)}>
                Annuler
              </button>
              <button type="button" className="ceq-modal-save" onClick={saveBulkChanges}
                disabled={bulkSaving || fieldsToUpdate.size === 0}>
                {bulkSaving ? 'Enregistrement…' : `Appliquer à ${selectedUsageIds.size} usage${selectedUsageIds.size > 1 ? 's' : ''} →`}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════
          Edit usage modal
      ════════════════════════════════════════ */}
      {editingUsage && (
        <>
          <div className="ceq-modal-backdrop" onClick={handleCancelEdit} />
          <div className="ceq-modal cus-edit-modal" role="dialog" aria-modal="true">

            {/* Header */}
            <div className="ceq-modal-header">
              <div>
                <p className="ceq-modal-header-label">
                  {editEqMeta?.icon} {isCreatingUsage ? 'Nouvel usage' : 'Modifier l\'usage'}
                </p>
                <h2 className="ceq-modal-header-title">
                  {editingUsage.name_section}
                  <span className="ceq-modal-breadcrumb-sep"> › </span>
                  {editingUsage.name_room}
                </h2>
              </div>
              <div className="cus-header-right">
                {/* Unité fonctionnelle — étiquette colorée, modifiable directement au clic
                    (c'est un <select> natif stylé en tag). Par défaut à la création, elle
                    reprend celle de l'équipement parent (voir buildNewUsage). */}
                <div className="cus-fu-tag-row">
                  <select
                    className={`reorg-fu-tag reorg-fu-tag--usage${!fuMatch && !isCustomFU ? ' reorg-fu-tag--empty' : ''}`}
                    style={getFunctionalUnitTagStyle(editingUsage.functional_unit, buildingFunctionalUnits)}
                    value={fuMatch ? editingUsage.functional_unit : (isCustomFU ? '__custom__' : '')}
                    onChange={e => {
                      if (e.target.value === '__custom__') patch('functional_unit', '');
                      else patch('functional_unit', e.target.value);
                    }}
                    title="Unité fonctionnelle"
                  >
                    <option value="">Non catégorisé</option>
                    {buildingFunctionalUnits.map(fu => (
                      <option key={fu.id} value={fu.id}>{fu.name}</option>
                    ))}
                    <option value="__custom__">+ Texte libre…</option>
                  </select>
                  {isCustomFU && (
                    <input className="cus-text-input cus-text-input--sub cus-fu-custom-input"
                      value={editingUsage.functional_unit || ''}
                      onChange={e => patch('functional_unit', e.target.value)}
                      placeholder="Ex: Bureau, Atelier…" />
                  )}
                </div>
                <button type="button" className="ceq-modal-close"
                  onClick={handleCancelEdit} aria-label="Fermer">✕</button>
              </div>
            </div>

            {/* Live volume preview */}
            <div className="cus-edit-preview">
              <div className="cus-edit-preview-content">
                <span className="cus-edit-preview-vol">
                  {Number(liveVolume).toFixed(1)}
                  <span className="cus-edit-preview-unit"> m³/an</span>
                </span>
                <span className="cus-edit-preview-label">Volume estimé en temps réel</span>
              </div>
            </div>

            {/* Scrollable body */}
            <div className="ceq-modal-body cus-edit-body">

              {/* ── Catégorie ── */}
              <div className="cus-form-section">
                <div className="ceq-modal-section-title">
                  <span className="cus-step-badge">1</span>
                  <span>Catégorie</span>
                </div>

                {/* Type d'usage — catégorie / sous-catégorie */}
                <div className="cus-form-field">
                  <label className="cus-form-label">Type d'usage</label>
                  <UsageCategoryPicker
                    categoryId={editingUsage.usage_category_id}
                    subcategoryId={editingUsage.usage_subcategory_id}
                    buildingId={building_id}
                    buildingType={buildingData?.type_building}
                    onChange={(categoryId, subcategoryId, subcategoryLabel) => {
                      patch('usage_category_id', categoryId);
                      patch('usage_subcategory_id', subcategoryId);
                      // Aperçu client — le serveur re-dérive systématiquement type_usage
                      // depuis usage_subcategory_id à l'écriture (voir DB_processor.py).
                      patch('type_usage', subcategoryLabel || '');
                    }}
                  />
                </div>
              </div>

              {/* ── Utilisation ── */}
              <div className="cus-form-section">
                <div className="ceq-modal-section-title">
                  <span className="cus-step-badge">2</span>
                  <span>Utilisation</span>
                </div>

                {/* Point d'eau regroupant plusieurs équipements (ex. 3 douches sur une même fiche) —
                    avertissement + choix de l'échelle de saisie, même logique que l'assistant d'audit
                    (UsageForm.js : nb_users/daily_usage restent stockés à l'échelle du point d'eau entier,
                    ce choix ne fait que diviser/multiplier l'affichage pour la saisie). */}
                {nbEquipments > 1 && (
                  <div className="cus-multi-eq-warning">
                    <InfoIcon />
                    <span>
                      Ce point d'eau regroupe <strong>{nbEquipments} équipements</strong> du même type.
                      Choisissez si vous saisissez les données pour un seul équipement ou pour l'ensemble.
                    </span>
                  </div>
                )}

                {nbEquipments > 1 && (
                  <div className="cus-form-field">
                    <label className="cus-form-label">Échelle de saisie</label>
                    <div className="cus-toggle-group">
                      <button type="button"
                        className={`cus-toggle-btn${singleEquipmentView ? ' cus-toggle-btn--active' : ''}`}
                        onClick={() => setSingleEquipmentView(true)}>
                        Par point d'eau
                      </button>
                      <button type="button"
                        className={`cus-toggle-btn${!singleEquipmentView ? ' cus-toggle-btn--active' : ''}`}
                        onClick={() => setSingleEquipmentView(false)}>
                        Tous les {nbEquipments} équipements
                      </button>
                    </div>
                  </div>
                )}

                {/* Type d'usager — lecture seule, uniquement s'il a été assigné (via la répartition des usagers) */}
                {editingUsage.user_name && editingUsage.user_name !== 'none' && (
                  <div className="cus-form-field cus-form-field--row">
                    <label className="cus-form-label">Type d'usager</label>
                    <span className="cus-usage-user-badge cus-usage-user-badge--form">{editingUsage.user_name}</span>
                  </div>
                )}

                {/* Mode de calcul */}
                <div className="cus-form-field">
                  <label className="cus-form-label">Mode de calcul</label>
                  <div className="cus-toggle-group">
                    <button type="button"
                      className={`cus-toggle-btn${(ud.daily_usage_type || 'per_person') === 'per_person' ? ' cus-toggle-btn--active' : ''}`}
                      onClick={() => patchData('daily_usage_type', 'per_person')}>
                      Par personne
                    </button>
                    <button type="button"
                      className={`cus-toggle-btn${(ud.daily_usage_type || 'per_person') === 'total' ? ' cus-toggle-btn--active' : ''}`}
                      onClick={() => patchData('daily_usage_type', 'total')}>
                      Global
                    </button>
                  </div>
                </div>

                {/* nb_users — only if per_person, champ à remplir. Aligné en ligne (libellé, champ, unité) ;
                    la ligne passe à la ligne suivante seulement si l'espace vient à manquer.
                    Valeur toujours stockée à l'échelle du point d'eau entier ; en vue "par point d'eau"
                    on affiche/saisit la valeur divisée par nb_equipment (comme UsageForm.js). */}
                {(ud.daily_usage_type || 'per_person') === 'per_person' && (
                  <div className="cus-form-field cus-form-field--row cus-form-field--tight">
                    <label className="cus-form-label">
                      Nombre de personnes{singleEquipmentView && nbEquipments > 1 ? ' (par point d\'eau)' : ''}
                    </label>
                    <div className="cus-number-field">
                      <input type="number" className="cus-text-input cus-text-input--compact"
                        min={0} step={1}
                        value={singleEquipmentView && nbEquipments > 1
                          ? Math.round(((ud.nb_users || 0) / nbEquipments) * 100) / 100
                          : (ud.nb_users ?? '')}
                        onChange={e => {
                          const v = e.target.value === '' ? 0 : Math.max(0, Number(e.target.value));
                          patchData('nb_users', singleEquipmentView && nbEquipments > 1 ? v * nbEquipments : v);
                        }} />
                      <span className="cus-number-field-suffix">pers.</span>
                    </div>
                  </div>
                )}

                {/* daily_usage — champ à remplir, unité explicite selon le type d'équipement (durée en minutes ou nombre de cycles).
                    La bascule par point d'eau ne s'applique que si daily_usage_type = 'total' : en 'per_person'
                    la valeur est déjà une quantité par personne, indépendante du nombre d'équipements. */}
                <div className="cus-form-field cus-form-field--row cus-form-field--tight">
                  <div>
                    <label className="cus-form-label">
                      {dailyLabel}{scaleDailyUsage ? " (par point d'eau)" : ''}
                    </label>
                    <span className="cus-form-hint">
                      {DAILY_USAGE_HINTS[editingUsage.equipment_type] || ''}
                      {(ud.daily_usage_type || 'per_person') === 'per_person' ? ' · par personne' : ''}
                    </span>
                  </div>
                  <div className="cus-number-field">
                    <input type="number" className="cus-text-input cus-text-input--compact"
                      min={0} step={DAILY_USAGE_STEP[editingUsage.equipment_type] ?? 1}
                      value={scaleDailyUsage
                        ? Math.round(((ud.daily_usage || 0) / nbEquipments) * 100) / 100
                        : (ud.daily_usage ?? '')}
                      onChange={e => {
                        const v = e.target.value === '' ? 0 : Math.max(0, Number(e.target.value));
                        patchData('daily_usage', scaleDailyUsage ? v * nbEquipments : v);
                      }} />
                  </div>
                </div>
                {nbEquipments > 1 && isTotalDailyUsage && (
                  <p className="cus-form-hint cus-form-hint--warning">
                    {singleEquipmentView
                      ? `Valeur pour un seul point d'eau sur les ${nbEquipments}.`
                      : `Valeur répartie sur les ${nbEquipments} points d'eau.`}
                  </p>
                )}

                {/* Jours par semaine — champ à remplir */}
                <div className="cus-form-field cus-form-field--row cus-form-field--tight">
                  <label className="cus-form-label">Jours par semaine</label>
                  <div className="cus-number-field">
                    <input type="number" className="cus-text-input cus-text-input--compact"
                      min={0} max={7} step={1}
                      value={ud.weekly_usage ?? ''}
                      onChange={e => patchData('weekly_usage', e.target.value === '' ? 0 : Math.min(7, Math.max(0, Number(e.target.value))))} />
                    <span className="cus-number-field-suffix">j / sem.</span>
                  </div>
                </div>

                {/* Semaines par an — champ à remplir */}
                <div className="cus-form-field cus-form-field--row cus-form-field--tight">
                  <label className="cus-form-label">Semaines par an</label>
                  <div className="cus-number-field">
                    <input type="number" className="cus-text-input cus-text-input--compact"
                      min={0} max={52} step={1}
                      value={ud.yearly_usage ?? ''}
                      onChange={e => patchData('yearly_usage', e.target.value === '' ? 0 : Math.min(52, Math.max(0, Number(e.target.value))))} />
                    <span className="cus-number-field-suffix">sem. / an</span>
                  </div>
                </div>

                {/* Semaines d'activité intense/réduite — repliées par défaut, juste sous "Semaines par an" */}
                <button type="button" className="cus-mini-toggle"
                  onClick={() => setShowIntenseWeeks(p => !p)}>
                  <ChevronIcon open={showIntenseWeeks} />
                  <span>Semaines d'activité intense / réduite</span>
                </button>

                {showIntenseWeeks && (
                  <>
                    <div className="cus-form-field cus-form-field--row cus-form-field--tight">
                      <label className="cus-form-label">Sem. d'activité intense</label>
                      <div className="cus-number-field">
                        <input type="number" className="cus-text-input cus-text-input--compact"
                          min={0} max={maxIntense} step={1}
                          value={ud.more_intense_weeks ?? ''}
                          onChange={e => patchData('more_intense_weeks', e.target.value === '' ? 0 : Math.min(maxIntense, Math.max(0, Number(e.target.value))))} />
                        <span className="cus-number-field-suffix">sem.</span>
                      </div>
                    </div>

                    <div className="cus-form-field cus-form-field--row cus-form-field--tight">
                      <label className="cus-form-label">Sem. d'activité réduite</label>
                      <div className="cus-number-field">
                        <input type="number" className="cus-text-input cus-text-input--compact"
                          min={0} max={maxLess} step={1}
                          value={ud.less_intense_weeks ?? ''}
                          onChange={e => patchData('less_intense_weeks', e.target.value === '' ? 0 : Math.min(maxLess, Math.max(0, Number(e.target.value))))} />
                        <span className="cus-number-field-suffix">sem.</span>
                      </div>
                    </div>

                    {(ud.yearly_usage || 0) > 0 && (
                      <div className="cus-weeks-breakdown">
                        {(() => {
                          const total = ud.yearly_usage || 0;
                          const more  = ud.more_intense_weeks || 0;
                          const less  = ud.less_intense_weeks || 0;
                          const normal = Math.max(0, total - more - less);
                          return (
                            <>
                              <div className="cus-weeks-bar">
                                <div className="cus-weeks-seg cus-weeks-seg--normal"  style={{ flex: normal }} title={`${normal} sem. normales`} />
                                <div className="cus-weeks-seg cus-weeks-seg--intense" style={{ flex: more }}   title={`${more} sem. intenses`} />
                                <div className="cus-weeks-seg cus-weeks-seg--reduced" style={{ flex: less }}   title={`${less} sem. réduites`} />
                              </div>
                              <div className="cus-weeks-legend">
                                <span><span className="cus-weeks-dot cus-weeks-dot--normal" />{normal} normales</span>
                                {more > 0 && <span><span className="cus-weeks-dot cus-weeks-dot--intense" />+{more} intenses</span>}
                                {less > 0 && <span><span className="cus-weeks-dot cus-weeks-dot--reduced" />−{less} réduites</span>}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </>
                )}

                {usageStats && (
                  <div className="cus-live-calc">
                    ≈ {Math.round(usageStats.annualAmount)} {annualLabel}, soit {usageStats.perDayAverage.toFixed(1)} / jour en moyenne
                  </div>
                )}
              </div>

              {/* ── Eau ── */}
              <div className="cus-form-section">
                <div className="ceq-modal-section-title">
                  <span className="cus-step-badge">3</span>
                  <span>Eau</span>
                </div>

                <div className="cus-form-field">
                  <label className="cus-form-label">Part d'eau chaude</label>
                  {editingHotWater && !hotWaterNotConnected ? (
                    <HotWaterRatioInput
                      value={hotWater.value}
                      onChange={(newValue) => patch('hot_water_ratio', newValue)}
                      presets={HOT_WATER_PRESETS[editingUsage.equipment_type]}
                    />
                  ) : (
                    <div className="cus-hwr-collapsed">
                      <span className="cus-hwr-collapsed-value">{hotWater.value} %</span>
                      <span className="cus-hwr-source">{HOT_WATER_SOURCE_LABELS[hotWater.source]}</span>
                      {!hotWaterNotConnected && (
                        <button type="button" className="cus-hwr-edit-link" onClick={() => setEditingHotWater(true)}>
                          Modifier
                        </button>
                      )}
                    </div>
                  )}
                  {!hotWaterNotConnected && editingUsage.hot_water_ratio != null && (
                    <button
                      type="button"
                      className="cus-hwr-edit-link cus-hwr-reset-link"
                      onClick={() => { patch('hot_water_ratio', null); setEditingHotWater(false); }}
                    >
                      Reprendre la part de l'équipement
                    </button>
                  )}
                </div>

                <div className="cus-form-field">
                  <label className="cus-form-label">Eau en entrée</label>
                  <div className="cus-toggle-group">
                    <button type="button"
                      className={`cus-toggle-btn${editingUsage.water_quality_needed === 'drinkable' ? ' cus-toggle-btn--active' : ''}`}
                      onClick={() => patch('water_quality_needed', 'drinkable')}>Potable</button>
                    <button type="button"
                      className={`cus-toggle-btn${editingUsage.water_quality_needed === 'non_drinkable' ? ' cus-toggle-btn--active' : ''}`}
                      onClick={() => patch('water_quality_needed', 'non_drinkable')}>Non potable</button>
                  </div>
                </div>
              </div>

              {/* ── Hypothèses et sources ── */}
              <div className="cus-form-section">
                <div className="ceq-modal-section-title">
                  <span className="cus-step-badge">4</span>
                  <span>Hypothèses et sources</span>
                </div>
                <div className="cus-form-field">
                  {editingUsage.distribution_id ? (
                    <div className="cus-form-hint">
                      Usage issu d'un lot de répartition : ses hypothèses se notent sur le lot ou le profil d'usagers
                      (une note ici serait perdue au prochain recalcul du lot).
                    </div>
                  ) : (
                    <textarea className="cus-text-input" rows={3} value={editingUsage.notes ?? ''}
                      placeholder="Ex. : 4 j/sem, 2 cycles/jour — dit par le gérant (visite du 12/10)"
                      onChange={e => patch('notes', e.target.value)} />
                  )}
                </div>
              </div>

              {/* ── Informations complémentaires (collapsible) ── */}
              <div className="cus-form-section cus-form-section--last">
                <button type="button" className="cus-advanced-toggle"
                  onClick={() => setShowAdvancedFields(p => !p)}>
                  <span className="cus-step-badge">5</span>
                  <span>Informations complémentaires</span>
                  <ChevronIcon open={showAdvancedFields} />
                </button>

                {showAdvancedFields && (
                  <div className="cus-advanced-body">
                    <div className="cus-form-field">
                      <label className="cus-form-label">Eau en sortie</label>
                      <div className="cus-toggle-group">
                        <button type="button"
                          className={`cus-toggle-btn${editingUsage.water_quality_out === 'drinkable' ? ' cus-toggle-btn--active' : ''}`}
                          onClick={() => patch('water_quality_out', 'drinkable')}>Potable</button>
                        <button type="button"
                          className={`cus-toggle-btn${editingUsage.water_quality_out === 'non_drinkable' ? ' cus-toggle-btn--active' : ''}`}
                          onClick={() => patch('water_quality_out', 'non_drinkable')}>Non potable</button>
                      </div>
                    </div>

                    <div className="cus-form-field cus-form-field--row">
                      <label className="cus-form-label">Économies possibles</label>
                      <button type="button"
                        className={`cus-switch${editingUsage.possible_savings ? ' cus-switch--on' : ''}`}
                        onClick={() => patch('possible_savings', !editingUsage.possible_savings)}
                        aria-label="Économies possibles">
                        <span className="cus-switch-thumb" />
                      </button>
                    </div>

                    <div className="cus-form-field cus-form-field--row">
                      <label className="cus-form-label">Volume connu (saisie directe)</label>
                      <button type="button"
                        className={`cus-switch${editingUsage.is_known_volume ? ' cus-switch--on' : ''}`}
                        onClick={() => patch('is_known_volume', !editingUsage.is_known_volume)}
                        aria-label="Volume connu">
                        <span className="cus-switch-thumb" />
                      </button>
                    </div>

                    {editingUsage.is_known_volume && (
                      <div className="cus-form-field cus-form-field--row">
                        <label className="cus-form-label">Volume annuel (m³)</label>
                        <div className="cus-stepper">
                          <button type="button" className="cus-stepper-btn"
                            onClick={() => patch('annual_volume', Math.max(0, Math.round(((editingUsage.annual_volume || 0) - 1) * 10) / 10))}>−</button>
                          <input type="number" className="cus-stepper-input"
                            value={editingUsage.annual_volume ?? ''}
                            min={0} step={1}
                            onChange={e => patch('annual_volume', e.target.value === '' ? 0 : Number(e.target.value))} />
                          <button type="button" className="cus-stepper-btn"
                            onClick={() => patch('annual_volume', Math.round(((editingUsage.annual_volume || 0) + 1) * 10) / 10)}>+</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

            </div>

            {/* Sticky footer */}
            <div className="ceq-modal-footer ceq-modal-footer--split">
              {!isCreatingUsage ? (
                <button type="button" className="cus-delete-link-btn"
                  onClick={requestDeleteEditing} disabled={saving}>
                  🗑 Supprimer cet usage
                </button>
              ) : <span />}
              <div className="ceq-modal-footer-actions">
                <button type="button" className="ceq-modal-cancel"
                  onClick={handleCancelEdit} disabled={saving}>
                  Annuler
                </button>
                <button type="button" className="cus-save-btn"
                  onClick={handleSaveUsage} disabled={saving}>
                  {saving ? 'Enregistrement…' : isCreatingUsage ? 'Créer l\'usage' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Equipment info modal ── */}
      {infoEquipment && (
        <>
          <div className="ceq-modal-backdrop" onClick={closeEquipmentInfo} />
          <div className="ceq-modal" role="dialog" aria-modal="true">
            <div className="ceq-modal-header">
              <div>
                <p className="ceq-modal-header-label">
                  {EQUIPMENT_META[infoEquipment.type_equipment]?.icon}{' '}
                  {EQUIPMENT_META[infoEquipment.type_equipment]?.label || infoEquipment.type_equipment}
                </p>
                <h2 className="ceq-modal-header-title">
                  {infoEquipment.location?.name_section}
                  <span className="ceq-modal-breadcrumb-sep"> › </span>
                  {infoEquipment.location?.name_room}
                  {infoEquipment.location?.room_multiplier > 1 && (
                    <span
                      className="cus-room-multiplier-badge"
                      style={{ marginLeft: 8 }}
                      title={`Ce point d'eau est répété dans ${infoEquipment.location.room_multiplier} pièces identiques : les volumes affichés sont déjà multipliés en conséquence.`}
                    >
                      ×{infoEquipment.location.room_multiplier}
                    </span>
                  )}
                </h2>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 16 }}>
                {!equipmentEditMode && (
                  <button type="button" className="ceq-bulk-mode-btn" onClick={startEditEquipment}>
                    ✎ Modifier
                  </button>
                )}
                <button type="button" className="ceq-modal-close" style={{ margin: 0 }}
                  onClick={closeEquipmentInfo} aria-label="Fermer">✕</button>
              </div>
            </div>

            <div className="ceq-modal-body">
              <div className="ceq-modal-visual">
                <div className="ceq-modal-photo">
                  {infoEquipment.image_url
                    ? <img src={infoEquipment.image_url} alt="Équipement" className="ceq-modal-photo-img" />
                    : (
                      <div className="ceq-modal-photo-placeholder">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                          <polyline points="21 15 16 10 5 21"/>
                        </svg>
                      </div>
                    )
                  }
                </div>
                <div className="ceq-modal-metrics">
                  <div className="ceq-modal-metric-grid">
                    <div>
                      <p className="ceq-modal-metric-label">Consommation annuelle</p>
                      <p className="ceq-modal-metric-big">
                        {Number(infoEquipment.total_real_consumption || 0).toFixed(1)}
                        <span className="ceq-modal-metric-unit"> m³</span>
                      </p>
                    </div>
                    {infoEquipment.water_value != null && (
                      <div>
                        <p className="ceq-modal-metric-label">
                          {infoEquipment.water_value_unit?.includes('min') ? 'Débit' : 'Volume'}
                        </p>
                        <p className="ceq-modal-metric-big">
                          {infoEquipment.water_value}
                          <span className="ceq-modal-metric-unit"> {infoEquipment.water_value_unit || ''}</span>
                        </p>
                      </div>
                    )}
                    {infoEquipment.technical_data?.nb_equipment != null && (
                      <div>
                        <p className="ceq-modal-metric-label">Nombre</p>
                        <p className="ceq-modal-metric-big">{infoEquipment.technical_data.nb_equipment}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="ceq-modal-section-title" style={{ marginTop: 4 }}>
                <span className="ceq-filter-accent-bar" />
                <span>Données techniques</span>
              </div>

              <div className="cus-modal-fields" style={{ marginTop: 12 }}>
                {infoEquipment.technical_data &&
                  Object.entries(infoEquipment.technical_data)
                    .filter(([key]) => FIELD_CONFIG[key] && !FIELD_CONFIG[key].hidden && key !== 'nb_equipment')
                    .map(([key, value]) => {
                      const cfg = FIELD_CONFIG[key];

                      if (equipmentEditMode) {
                        return (
                          <div key={key}
                            className={`cus-modal-field cus-modal-field--edit${cfg.type === 'textarea' ? ' cus-modal-field--full' : ''}`}>
                            <span className="cus-modal-field-label">{cfg.label}</span>
                            <EquipmentFieldInput
                              fieldKey={key}
                              value={equipmentEditData?.[key]}
                              machineTypes={machineTypes}
                              onChange={v => patchEquipmentField(key, v)}
                            />
                          </div>
                        );
                      }

                      const displayVal = formatFieldValue(key, value, infoEquipment.technical_data, machineTypes);
                      if (!displayVal || displayVal === '') return null;
                      return (
                        <div key={key} className="cus-modal-field">
                          <span className="cus-modal-field-label">{cfg.label}</span>
                          <span className="cus-modal-field-value">{displayVal}</span>
                        </div>
                      );
                    })
                }
              </div>

              {equipmentSaveError && (
                <div className="ceq-modal-msg ceq-modal-msg--error" style={{ marginTop: 12 }}>{equipmentSaveError}</div>
              )}
            </div>

            <div className="ceq-modal-footer">
              {equipmentEditMode ? (
                <>
                  <button type="button" className="ceq-modal-cancel"
                    onClick={cancelEditEquipment} disabled={equipmentSaving}>Annuler</button>
                  <button type="button" className="cus-save-btn"
                    onClick={saveEquipmentEdit} disabled={equipmentSaving}>
                    {equipmentSaving ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                </>
              ) : (
                <button type="button" className="ceq-modal-cancel"
                  onClick={closeEquipmentInfo}>Fermer</button>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Delete confirmation modal (individual or bulk) ── */}
      {deleteTarget && (
        <>
          <div className="ceq-modal-backdrop" onClick={cancelDelete} />
          <div className="ceq-modal" role="alertdialog" aria-modal="true" aria-describedby="cus-delete-warning">
            <div className="ceq-modal-header">
              <div>
                <p className="ceq-modal-header-label">Suppression</p>
                <h2 className="ceq-modal-header-title">
                  {deleteTarget.ids.length > 1
                    ? `Supprimer ${deleteTarget.ids.length} usages ?`
                    : 'Supprimer cet usage ?'}
                </h2>
              </div>
              <button type="button" className="ceq-modal-close" onClick={cancelDelete} aria-label="Fermer">✕</button>
            </div>

            <div className="ceq-modal-body">
              <div className="ceq-danger-warning" id="cus-delete-warning">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                  <path d="M8 2L14.5 13.5H1.5L8 2Z" stroke="#dc2626" strokeWidth="1.4" strokeLinejoin="round"/>
                  <path d="M8 6.5V9" stroke="#dc2626" strokeWidth="1.4" strokeLinecap="round"/>
                  <circle cx="8" cy="11" r="0.75" fill="#dc2626"/>
                </svg>
                <p>
                  Cette action est <strong>définitive et irréversible</strong>. {deleteTarget.ids.length > 1 ? 'Les usages sélectionnés et leur' : "L'usage et son"} historique de consommation seront perdus.
                </p>
              </div>

              <div className="cus-delete-list">
                {deleteTarget.usages.slice(0, 8).map(u => (
                  <div key={u._id} className="cus-delete-list-item">
                    <span className="cus-usage-dot" style={{ background: usageTypeColors[u.type_usage] || '#94a3b8' }} />
                    <span className="cus-delete-list-type">{u.type_usage || '—'}</span>
                    <span className="cus-delete-list-loc">{u.name_section || '—'} › {u.name_room || '—'}</span>
                    <span className="cus-delete-list-vol">{Number(u.annual_volume || 0).toFixed(1)} m³/an</span>
                  </div>
                ))}
                {deleteTarget.usages.length > 8 && (
                  <div className="cus-delete-list-more">
                    + {deleteTarget.usages.length - 8} autre{deleteTarget.usages.length - 8 > 1 ? 's' : ''}
                  </div>
                )}
              </div>

              {deleteError && (
                <div className="ceq-modal-msg ceq-modal-msg--error" style={{ marginTop: 12 }}>{deleteError}</div>
              )}
            </div>

            <div className="ceq-modal-footer">
              <button type="button" className="ceq-modal-cancel" onClick={cancelDelete} disabled={deleting}>
                Annuler
              </button>
              <button type="button" className="cus-delete-confirm-btn" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Suppression…' : `Supprimer définitivement${deleteTarget.ids.length > 1 ? ` (${deleteTarget.ids.length})` : ''}`}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClientUsages;
